import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { format, subDays } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
  FileText, Store, Calendar, RefreshCw, Filter, Search,
  Eye, ChevronRight, User, Clock, ArrowRight, AlertTriangle,
  Package, Cloud, CheckCircle, XCircle, Loader2, MoreHorizontal, ShoppingCart, AlertCircle,
  ShieldAlert,
} from 'lucide-react';
import SkuExceptions, { fetchUnresolvedCount } from './SkuExceptions';
import QuarantineDocuments from './QuarantineDocuments';
import StockIssues, { fetchStockIssuesCount } from './StockIssues';
import OneDriveFiles from './OneDriveFiles';
import WinTouchAutomation from './WinTouchAutomation';
import SageErrors from './SageErrors';
import { useStoreMapping } from '@/hooks/useStoreMapping';

// Action type labels and colors
const ACTION_CONFIG = {
  CREATED: { label: 'Criado', color: 'bg-blue-100 text-blue-700' },
  UPDATED: { label: 'Atualizado', color: 'bg-slate-100 text-slate-700' },
  CONFIRMED: { label: 'Confirmado', color: 'bg-emerald-100 text-emerald-700' },
  REOPENED: { label: 'Reaberto', color: 'bg-purple-100 text-purple-700' },
  CANCELLED: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
  COUNTED_CASH_CHANGED: { label: 'Numerário Alterado', color: 'bg-amber-100 text-amber-700' },
  TPA_AMOUNT_CHANGED: { label: 'TPA Alterado', color: 'bg-amber-100 text-amber-700' },
  ALLOCATION_ADDED: { label: 'Alocação Adicionada', color: 'bg-blue-100 text-blue-700' },
  ALLOCATION_REMOVED: { label: 'Alocação Removida', color: 'bg-orange-100 text-orange-700' },
  POST_CLOSE_DATA_DETECTED: { label: 'Dados Alterados', color: 'bg-red-100 text-red-700' },
  CONFIG_CHANGED: { label: 'Config Alterada', color: 'bg-slate-100 text-slate-700' },
};

// Entity type labels
const ENTITY_LABELS = {
  closure: 'Fecho',
  deposit: 'Depósito',
  config: 'Configuração',
};

// Action badge component
const ActionBadge = ({ action }) => {
  const config = ACTION_CONFIG[action] || { label: action, color: 'bg-slate-100 text-slate-700' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// Value diff display
const ValueDiff = ({ before, after }) => {
  if (!before && !after) return <span className="text-slate-400">—</span>;
  
  return (
    <div className="text-xs space-y-1">
      {before && (
        <div className="text-red-600 line-through">
          {typeof before === 'object' ? JSON.stringify(before, null, 2) : String(before)}
        </div>
      )}
      {after && (
        <div className="text-emerald-600">
          {typeof after === 'object' ? JSON.stringify(after, null, 2) : String(after)}
        </div>
      )}
    </div>
  );
};

// Audit detail dialog
const AuditDetailDialog = ({ entry, getStoreName }) => {
  if (!entry) return null;
  
  // Fallback for store name if getStoreName not provided
  const storeName = getStoreName ? getStoreName(entry.store_code) : entry.store_code;
  
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Eye className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalhe do Registo de Auditoria</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <Label className="text-slate-500">Data/Hora</Label>
              <p className="font-medium">
                {entry.timestamp ? format(new Date(entry.timestamp), "dd/MM/yyyy HH:mm:ss", { locale: pt }) : '—'}
              </p>
            </div>
            <div>
              <Label className="text-slate-500">Utilizador</Label>
              <p className="font-medium">{entry.user_email || entry.user_id || '—'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Loja</Label>
              <p className="font-medium">{storeName || '—'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Ação</Label>
              <ActionBadge action={entry.action} />
            </div>
            <div>
              <Label className="text-slate-500">Tipo de Entidade</Label>
              <p className="font-medium">{ENTITY_LABELS[entry.entity_type] || entry.entity_type}</p>
            </div>
            <div>
              <Label className="text-slate-500">ID da Entidade</Label>
              <p className="font-mono text-xs">{entry.entity_id}</p>
            </div>
          </div>
          
          {entry.reason && (
            <div>
              <Label className="text-slate-500">Motivo</Label>
              <p className="mt-1 p-2 bg-slate-50 rounded text-sm">{entry.reason}</p>
            </div>
          )}
          
          {(entry.before_value || entry.after_value) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Valor Anterior</Label>
                <pre className="mt-1 p-2 bg-red-50 rounded text-xs overflow-x-auto max-h-48">
                  {entry.before_value ? JSON.stringify(entry.before_value, null, 2) : '—'}
                </pre>
              </div>
              <div>
                <Label className="text-slate-500">Valor Novo</Label>
                <pre className="mt-1 p-2 bg-emerald-50 rounded text-xs overflow-x-auto max-h-48">
                  {entry.after_value ? JSON.stringify(entry.after_value, null, 2) : '—'}
                </pre>
              </div>
            </div>
          )}
          
          {entry.metadata && Object.keys(entry.metadata).length > 0 && (
            <div>
              <Label className="text-slate-500">Metadados</Label>
              <pre className="mt-1 p-2 bg-slate-50 rounded text-xs overflow-x-auto">
                {JSON.stringify(entry.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function RetailAuditLog() {
  const { user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Store name mapping hook
  const { getStoreName, storeMapping } = useStoreMapping();
  
  // Tab state - support URL parameter for direct navigation
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'audit-log');
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [quarantineCount, setQuarantineCount] = useState(0);
  useEffect(() => {
    axios.get(`${API}/wintouch/quarantine`, { params: { status: 'PENDING' } }).then((r) => setQuarantineCount(r.data.pending || 0)).catch(() => {});
  }, []);
  
  // State
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);
  
  // OneDrive manual ingest state
  const [oneDriveLoading, setOneDriveLoading] = useState(false);
  const [oneDriveResult, setOneDriveResult] = useState(null);
  const [showOneDriveResult, setShowOneDriveResult] = useState(false);
  
  // Stage 2 SAGE processing state
  const [sageProcessingLoading, setSageProcessingLoading] = useState(false);
  const [showSageConfirmModal, setShowSageConfirmModal] = useState(false);
  const [sagePendingSummary, setSagePendingSummary] = useState(null);
  const [sageProcessingResult, setSageProcessingResult] = useState(null);
  const [showSageResult, setShowSageResult] = useState(false);
  const [pendingMovementsCount, setPendingMovementsCount] = useState(0);
  const [totalProcessableCount, setTotalProcessableCount] = useState(0);
  
  // Preview modal workflow states
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isProcessingFromPreview, setIsProcessingFromPreview] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [processResultMode, setProcessResultMode] = useState(false); // true = show result instead of preview
  
  // Unified workflow states (Stage 1 + Preview)
  const [unifiedIngestionResult, setUnifiedIngestionResult] = useState(null);
  const [unifiedWorkflowStep, setUnifiedWorkflowStep] = useState('idle'); // 'idle', 'ingesting', 'previewing', 'ready', 'processing', 'done', 'error'
  
  // Retail Sales Sync state
  const [retailSyncLoading, setRetailSyncLoading] = useState(false);
  const [retailSyncResult, setRetailSyncResult] = useState(null);
  const [showRetailSyncResult, setShowRetailSyncResult] = useState(false);
  
  // XML Ingestion (Stage 1) state - explicit button
  const [showIngestionConfirm, setShowIngestionConfirm] = useState(false);
  const [ingestionLoading, setIngestionLoading] = useState(false);
  const [ingestionResult, setIngestionResult] = useState(null);
  const [showIngestionResult, setShowIngestionResult] = useState(false);
  
  // WinTouch Batches (XML → SAGE traceability) state
  const [wintouchBatches, setWintouchBatches] = useState([]);
  const [wintouchBatchesLoading, setWintouchBatchesLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showBatchDetail, setShowBatchDetail] = useState(false);
  const [stockIssuesSearch, setStockIssuesSearch] = useState('');
  
  // WinTouch Batches - NEW: Sub-tabs and filters
  const [batchCategory, setBatchCategory] = useState(''); // '' = all, attention | pending | integrated
  const [batchStoreFilter, setBatchStoreFilter] = useState('');
  const [batchCounts, setBatchCounts] = useState({ attention: 0, pending: 0, integrated: 0, sku_errors: 0, stock_errors: 0 });
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchSkip, setBatchSkip] = useState(0);
  const [batchLimit] = useState(50);
  
  // Month filter - default to current month
  const [batchMonth, setBatchMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  
  // Batch detail issues state
  const [batchIssues, setBatchIssues] = useState(null);
  const [batchIssuesLoading, setBatchIssuesLoading] = useState(false);
  
  // SAGE Processing Preview/Dry-Run state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // Filters
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(searchParams.get('store') || '');
  const [entityType, setEntityType] = useState(searchParams.get('type') || '');
  const [action, setAction] = useState(searchParams.get('action') || '');
  const [entityId, setEntityId] = useState(searchParams.get('entity_id') || '');
  const [limit, setLimit] = useState(100);
  
  // Load unresolved count for SKU exceptions badge
  useEffect(() => {
    const loadUnresolvedCount = async () => {
      const count = await fetchUnresolvedCount();
      setUnresolvedCount(count);
    };
    loadUnresolvedCount();
  }, [activeTab]); // Refresh when switching tabs
  
  // Load stock issues count for badge
  const [stockIssuesCount, setStockIssuesCount] = useState(0);
  
  useEffect(() => {
    const loadStockIssuesCount = async () => {
      const count = await fetchStockIssuesCount();
      setStockIssuesCount(count);
    };
    loadStockIssuesCount();
  }, [activeTab]); // Refresh when switching tabs
  
  // Load pending movements count for Stage 2 button badge
  useEffect(() => {
    const loadPendingMovements = async () => {
      try {
        const res = await axios.get(`${API}/wintouch/pending-movements-summary`);
        setPendingMovementsCount(res.data.pending_batches || 0);
        // Use total_processable which includes Stock Issues
        setTotalProcessableCount(res.data.total_processable || 0);
      } catch (err) {
        console.error('Error loading pending movements:', err);
        setPendingMovementsCount(0);
        setTotalProcessableCount(0);
      }
    };
    loadPendingMovements();
    
    // Refresh periodically
    const interval = setInterval(loadPendingMovements, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);
  
  // Load stores
  useEffect(() => {
    const loadStores = async () => {
      try {
        const res = await axios.get(`${API}/retail-analytics/stores`);
        setStores(res.data.stores || []);
      } catch (err) {
        console.error('Error loading stores:', err);
      }
    };
    loadStores();
  }, []);
  
  // Load audit log
  const loadAuditLog = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (selectedStore) params.set('store_code', selectedStore);
      if (entityType) params.set('entity_type', entityType);
      if (entityId) params.set('entity_id', entityId);
      params.set('limit', limit.toString());
      
      const res = await axios.get(`${API}/retail-closures/audit-log?${params.toString()}`);
      let data = res.data || [];
      
      // Client-side filter by action if specified
      if (action) {
        data = data.filter(e => e.action === action);
      }
      
      setEntries(data);
    } catch (err) {
      console.error('Error loading audit log:', err);
      setError(err.response?.data?.detail || 'Erro ao carregar registos de auditoria');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadAuditLog();
  }, [selectedStore, entityType, entityId, limit]);
  
  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedStore) params.set('store', selectedStore);
    if (entityType) params.set('type', entityType);
    if (action) params.set('action', action);
    if (entityId) params.set('entity_id', entityId);
    setSearchParams(params, { replace: true });
  }, [selectedStore, entityType, action, entityId, setSearchParams]);
  
  // Clear filters
  const clearFilters = () => {
    setSelectedStore('');
    setEntityType('');
    setAction('');
    setEntityId('');
  };
  
  // Get unique actions from entries for filter dropdown
  const availableActions = useMemo(() => {
    const actions = new Set(entries.map(e => e.action));
    return Array.from(actions).sort();
  }, [entries]);
  
  // Filtered entries (by action)
  const filteredEntries = useMemo(() => {
    if (!action) return entries;
    return entries.filter(e => e.action === action);
  }, [entries, action]);
  
  // Handle tab change
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    const params = new URLSearchParams(searchParams);
    if (newTab === 'audit-log') {
      params.delete('tab');
    } else {
      params.set('tab', newTab);
    }
    setSearchParams(params, { replace: true });
    
    // Load WinTouch batches when switching to that tab
    if (newTab === 'wintouch-batches') {
      loadWintouchBatches();
    }
  };
  
  // Load WinTouch batches for XML → SAGE traceability
  const loadWintouchBatches = async (category = batchCategory, storeCode = batchStoreFilter, skip = 0, month = batchMonth) => {
    setWintouchBatchesLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', batchLimit.toString());
      params.set('skip', skip.toString());
      params.set('sort_order', 'asc'); // Chronological order (day 01→31)
      
      // Category filter (empty = all)
      if (category) params.set('category', category);
      if (storeCode) params.set('store_code', storeCode);
      
      // Month filter - calculate start_date and end_date
      if (month && month.year && month.month) {
        const startDate = `${month.year}-${String(month.month).padStart(2, '0')}-01`;
        const lastDay = new Date(month.year, month.month, 0).getDate();
        const endDate = `${month.year}-${String(month.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        params.set('start_date', startDate);
        params.set('end_date', endDate);
      }
      
      const res = await axios.get(`${API}/onedrive/batches?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setWintouchBatches(res.data.batches || []);
      setBatchCounts(res.data.counts || { attention: 0, pending: 0, integrated: 0, sku_errors: 0, stock_errors: 0 });
      setBatchTotal(res.data.total || 0);
      setBatchSkip(skip);
    } catch (err) {
      console.error('Error loading WinTouch batches:', err);
      setWintouchBatches([]);
      setBatchCounts({ attention: 0, pending: 0, integrated: 0, sku_errors: 0, stock_errors: 0 });
    } finally {
      setWintouchBatchesLoading(false);
    }
  };
  
  // Handle batch category change (now a filter, not a tab)
  const handleBatchCategoryChange = (newCategory) => {
    setBatchCategory(newCategory);
    setBatchSkip(0);
    loadWintouchBatches(newCategory, batchStoreFilter, 0, batchMonth);
  };
  
  // Handle batch store filter change
  const handleBatchStoreFilterChange = (storeCode) => {
    setBatchStoreFilter(storeCode);
    setBatchSkip(0);
    loadWintouchBatches(batchCategory, storeCode, 0, batchMonth);
  };
  
  // Handle month navigation
  const handleMonthChange = (direction) => {
    const newMonth = { ...batchMonth };
    if (direction === 'prev') {
      if (newMonth.month === 1) {
        newMonth.month = 12;
        newMonth.year -= 1;
      } else {
        newMonth.month -= 1;
      }
    } else {
      if (newMonth.month === 12) {
        newMonth.month = 1;
        newMonth.year += 1;
      } else {
        newMonth.month += 1;
      }
    }
    setBatchMonth(newMonth);
    setBatchSkip(0);
    loadWintouchBatches(batchCategory, batchStoreFilter, 0, newMonth);
  };
  
  // Handle batch pagination
  const handleBatchPageChange = (direction) => {
    const newSkip = direction === 'next' ? batchSkip + batchLimit : Math.max(0, batchSkip - batchLimit);
    setBatchSkip(newSkip);
    loadWintouchBatches(batchCategory, batchStoreFilter, newSkip, batchMonth);
  };
  
  // Format month name in Portuguese
  const getMonthName = (month) => {
    const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    return months[month - 1] || '';
  };
  
  // Get reason/situation for unified table view
  const getBatchReason = (batch) => {
    const reasons = batch._attention_reasons || [];
    if (batch._category === 'attention') {
      return reasons.length > 0 ? reasons.join(', ') : 'Necessita atenção';
    }
    if (batch._category === 'pending') {
      // Check what's missing
      const pending = [];
      if (batch.isl_required && !batch.isl_posted && !batch.sage_isl_reference) {
        pending.push('ISL pendente');
      }
      if (batch.iel_required && !batch.iel_posted && !batch.sage_iel_reference) {
        pending.push('IEL pendente');
      }
      return pending.length > 0 ? pending.join(', ') : 'Aguarda processamento';
    }
    // Integrated
    return 'Integrado';
  };
  
  // Load batch issues (stock issues + SKU exceptions)
  const loadBatchIssues = async (batchId) => {
    if (!batchId) return;
    setBatchIssuesLoading(true);
    try {
      const res = await axios.get(`${API}/onedrive/batch/${batchId}/issues`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setBatchIssues(res.data);
    } catch (err) {
      console.error('Error loading batch issues:', err);
      setBatchIssues(null);
    } finally {
      setBatchIssuesLoading(false);
    }
  };
  
  // Open batch detail modal
  const handleOpenBatchDetail = (batch) => {
    setSelectedBatch(batch);
    setShowBatchDetail(true);
    setReclassifyResult(null); // Reset reclassify result
    // Load issues for this batch
    if (batch?.batch_id) {
      loadBatchIssues(batch.batch_id);
    }
  };
  
  // Reclassify batch state
  const [reclassifyLoading, setReclassifyLoading] = useState(false);
  const [reclassifyResult, setReclassifyResult] = useState(null);
  
  // Force reclassify a batch
  const handleReclassifyBatch = async (batchId) => {
    setReclassifyLoading(true);
    setReclassifyResult(null);
    
    try {
      const res = await axios.post(`${API}/onedrive/batches/${batchId}/reclassify`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setReclassifyResult(res.data);
      
      // If successful, refresh the batch list and reload the batch issues
      if (res.data.success) {
        // Reload batch list
        loadWintouchBatches(batchCategory, batchStoreFilter, batchSkip, batchMonth);
        
        // Reload batch issues
        loadBatchIssues(batchId);
        
        // Update the selected batch with new data
        if (selectedBatch && selectedBatch.batch_id === batchId) {
          setSelectedBatch(prev => ({
            ...prev,
            status: res.data.new_status,
            unknown_skus: res.data.new_unknown_skus,
            has_exceptions: res.data.new_unknown_skus?.length > 0,
            _category: res.data.new_unknown_skus?.length > 0 ? 'attention' : (res.data.new_status === 'ready' ? 'pending' : prev._category),
          }));
        }
      }
    } catch (err) {
      console.error('Error reclassifying batch:', err);
      setReclassifyResult({
        success: false,
        error: err.response?.data?.detail || err.message || 'Erro ao reclassificar batch',
      });
    } finally {
      setReclassifyLoading(false);
    }
  };
  
  // Get status color for batch
  const getBatchStatusColor = (status, category) => {
    // Priority: use category color if available
    if (category === 'attention') return 'bg-red-100 text-red-800';
    if (category === 'integrated') return 'bg-emerald-100 text-emerald-800';
    if (category === 'pending') return 'bg-amber-100 text-amber-800';
    
    // Fallback to status
    switch (status) {
      case 'completed':
      case 'isl_posted':
      case 'iel_posted':
        return 'bg-emerald-100 text-emerald-800';
      case 'ready':
      case 'validated':
      case 'parsed':
      case 'received':
      case 'iel_pending':
        return 'bg-amber-100 text-amber-800';
      case 'needs_review':
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };
  
  // Get status label for batch
  const getBatchStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completo';
      case 'isl_posted': return 'ISL Criado';
      case 'iel_posted': return 'IEL Criado';
      case 'ready': return 'Pronto';
      case 'validated': return 'Validado';
      case 'parsed': return 'Processado';
      case 'received': return 'Recebido';
      case 'iel_pending': return 'IEL Pendente';
      case 'needs_review': return 'Necessita Revisão';
      case 'failed': return 'Falhou';
      default: return status || 'Desconhecido';
    }
  };
  
  // Get category label
  const getCategoryLabel = (category) => {
    switch (category) {
      case 'attention': return 'Atenção Necessária';
      case 'pending': return 'Pendentes';
      case 'integrated': return 'Integrados';
      default: return category;
    }
  };
  
  // OneDrive manual verification - uses same pipeline as scheduler
  const handleOneDriveCheck = async () => {
    setOneDriveLoading(true);
    setOneDriveResult(null);
    setShowOneDriveResult(false);
    
    try {
      const response = await axios.post(`${API}/onedrive/run-ingestion`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = response.data;
      const summary = data.summary || {};
      const fileResults = data.file_results || [];
      
      // Count results
      const newlyIngested = fileResults.filter(f => f.status === 'SUCCESS' || f.status === 'INGESTED').length;
      const alreadyProcessed = summary.files_already_processed || fileResults.filter(f => f.status === 'ALREADY_PROCESSED').length;
      const skippedCutoff = fileResults.filter(f => f.status === 'SKIPPED_CUTOFF').length;
      const failed = fileResults.filter(f => f.status === 'FAILED' || f.status === 'ERROR').length;
      const unknownSkus = data.aggregated?.sku_exceptions_created || 0;
      
      setOneDriveResult({
        success: data.success,
        filesDiscovered: summary.files_discovered || fileResults.length,
        newlyIngested,
        alreadyProcessed,
        skippedCutoff,
        failed,
        unknownSkus,
        timestamp: data.timestamp,
      });
      setShowOneDriveResult(true);
      
      // Refresh counts if new data was ingested
      if (newlyIngested > 0 || unknownSkus > 0) {
        const count = await fetchUnresolvedCount();
        setUnresolvedCount(count);
        const issuesCount = await fetchStockIssuesCount();
        setStockIssuesCount(issuesCount);
      }
      
    } catch (err) {
      console.error('OneDrive check failed:', err);
      setOneDriveResult({
        success: false,
        error: err.response?.data?.detail || err.message || 'Erro ao verificar OneDrive',
      });
      setShowOneDriveResult(true);
    } finally {
      setOneDriveLoading(false);
    }
  };
  
  // Check if user can run OneDrive check (admin or backoffice)
  const canRunOneDriveCheck = user?.role === 'admin' || user?.role === 'backoffice';
  
  // Check if user can run SAGE processing (admin only)
  const canRunSageProcessing = user?.role === 'admin';
  
  // Check if user can run XML ingestion (admin only)
  const canRunIngestion = user?.role === 'admin';
  
  // Handle XML Ingestion (Stage 1) - Opens confirmation modal first
  const handleOpenIngestionConfirm = () => {
    setShowIngestionConfirm(true);
  };
  
  // Poll ingestion job status
  const pollIngestionJob = async (jobId) => {
    const maxPolls = 120; // 10 minutes max (5s intervals)
    let polls = 0;
    
    while (polls < maxPolls) {
      try {
        const statusRes = await axios.get(`${API}/onedrive/run-ingestion/status/${jobId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        const jobStatus = statusRes.data;
        
        // Update progress in result state for visual feedback
        if (jobStatus.progress) {
          setIngestionResult({
            inProgress: true,
            progress: jobStatus.progress,
          });
          setShowIngestionResult(true);
        }
        
        if (jobStatus.status === 'COMPLETED') {
          // Job completed - return full result
          return {
            success: true,
            result: jobStatus.result,
          };
        } else if (jobStatus.status === 'FAILED') {
          return {
            success: false,
            error: jobStatus.error || 'Ingestão falhou',
          };
        }
        
        // Still running - wait and poll again
        await new Promise(resolve => setTimeout(resolve, 5000));
        polls++;
        
      } catch (err) {
        console.error('Error polling job status:', err);
        return {
          success: false,
          error: 'Erro ao verificar estado da ingestão',
        };
      }
    }
    
    return {
      success: false,
      error: 'Timeout: ingestão demorou demasiado tempo',
    };
  };
  
  // Execute XML Ingestion (Stage 1 ONLY - no SAGE writes) - ASYNC with job polling
  const handleExecuteIngestion = async () => {
    setShowIngestionConfirm(false);
    setIngestionLoading(true);
    setIngestionResult(null);
    
    try {
      // Start async job
      const startRes = await axios.post(`${API}/onedrive/run-ingestion/start`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const jobId = startRes.data.job_id;
      
      if (!jobId) {
        throw new Error(startRes.data.error || 'Não foi possível iniciar a ingestão');
      }
      
      // Poll for completion
      const pollResult = await pollIngestionJob(jobId);
      
      if (!pollResult.success) {
        setIngestionResult({
          success: false,
          error: pollResult.error,
        });
        setShowIngestionResult(true);
        return;
      }
      
      // Process successful result
      const data = pollResult.result || {};
      const summary = data.summary || {};
      const fileResults = data.file_results || [];
      const aggregated = data.aggregated || {};
      
      // Count results by status (include reclassified batches)
      const newlyIngested = fileResults.filter(f => 
        f.status === 'SUCCESS' || f.status === 'INGESTED'
      ).length;
      const reclassified = fileResults.filter(f => 
        f.status === 'RECLASSIFIED_RESOLVED' || f.status === 'RECLASSIFIED_STILL_PENDING'
      ).length;
      const alreadyProcessed = summary.files_already_processed || 
        fileResults.filter(f => f.status === 'ALREADY_PROCESSED').length;
      const failed = fileResults.filter(f => 
        f.status === 'FAILED' || f.status === 'ERROR'
      ).length;
      
      // Extract errors from failed files
      const errors = fileResults
        .filter(f => f.status === 'FAILED' || f.status === 'ERROR')
        .map(f => ({
          filename: f.filename,
          error: f.error || f.message || 'Erro desconhecido'
        }));
      
      // Extract reclassification info
      const reclassifiedInfo = fileResults
        .filter(f => f.status === 'RECLASSIFIED_RESOLVED' || f.status === 'RECLASSIFIED_STILL_PENDING')
        .map(f => ({
          filename: f.filename,
          batchId: f.batch_id,
          status: f.status,
          message: f.skipped_reason || f.message,
        }));
      
      setIngestionResult({
        success: data.success !== false,
        timestamp: data.timestamp,
        filesDiscovered: summary.files_discovered || fileResults.length,
        filesEligible: summary.files_eligible || 0,
        newlyIngested,
        reclassified,
        reclassifiedInfo,
        alreadyProcessed,
        failed,
        batchesCreated: aggregated.batches_created || 0,
        documentsCreated: aggregated.documents_created || 0,
        physicalLines: aggregated.physical_product_lines || 0,
        serviceLines: aggregated.service_lines || 0,
        financialLines: aggregated.financial_non_stock_lines || 0,
        unknownLines: aggregated.unknown_lines || 0,
        skuExceptions: aggregated.sku_exceptions_created || 0,
        errors,
      });
      setShowIngestionResult(true);
      
      // Refresh data if new content was ingested or reclassified
      if (newlyIngested > 0 || reclassified > 0 || aggregated.sku_exceptions_created > 0) {
        // Refresh unresolved count
        const count = await fetchUnresolvedCount();
        setUnresolvedCount(count);
        
        // Refresh stock issues count
        const issuesCount = await fetchStockIssuesCount();
        setStockIssuesCount(issuesCount);
        
        // Refresh pending movements count
        const pendingRes = await axios.get(`${API}/wintouch/pending-movements-summary`);
        setPendingMovementsCount(pendingRes.data.pending_batches || 0);
        setTotalProcessableCount(pendingRes.data.total_processable || 0);
        
        // Refresh WinTouch batches if on that tab
        if (activeTab === 'wintouch-batches') {
          loadWintouchBatches();
        }
      }
      
    } catch (err) {
      console.error('XML Ingestion failed:', err);
      setIngestionResult({
        success: false,
        error: err.response?.data?.detail || err.response?.data?.error || err.message || 'Erro ao executar ingestão',
      });
      setShowIngestionResult(true);
    } finally {
      setIngestionLoading(false);
    }
  };
  
  // Handle SAGE processing confirmation modal
  const handleOpenSageModal = async () => {
    try {
      const res = await axios.get(`${API}/wintouch/pending-movements-summary`);
      setSagePendingSummary(res.data);
      setShowSageConfirmModal(true);
    } catch (err) {
      console.error('Error loading pending summary:', err);
      setSagePendingSummary({ error: err.response?.data?.detail || 'Erro ao carregar resumo' });
      setShowSageConfirmModal(true);
    }
  };
  
  // Handle SAGE processing - ASYNC with polling to avoid timeout
  const handleSageProcessing = async () => {
    setShowSageConfirmModal(false);
    setSageProcessingLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // STEP 1: Start async processing job
      const startRes = await axios.post(`${API}/wintouch/process-pending-movements/start`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!startRes.data.success) {
        setSageProcessingResult({
          success: false,
          error: startRes.data.error || 'start_failed',
          message: startRes.data.message || 'Erro ao iniciar processamento',
        });
        setShowSageResult(true);
        setSageProcessingLoading(false);
        return;
      }
      
      const jobId = startRes.data.job_id;
      console.log(`SAGE processing job started: ${jobId}`);
      
      // STEP 2: Poll for job status
      let completed = false;
      let pollCount = 0;
      const maxPolls = 180; // 15 minutes max (5s intervals)
      
      while (!completed && pollCount < maxPolls) {
        await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds
        pollCount++;
        
        try {
          const statusRes = await axios.get(`${API}/wintouch/process-pending-movements/status/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const job = statusRes.data;
          console.log(`Job ${jobId}: ${job.progress?.message || job.status} (${job.progress?.percent || 0}%)`);
          
          if (job.status === 'COMPLETED') {
            completed = true;
            setSageProcessingResult(job.result);
            setShowSageResult(true);
            
            // Refresh ALL related data after processing
            try {
              const stockCount = await fetchStockIssuesCount();
              setStockIssuesCount(stockCount);
              
              const pendingRes = await axios.get(`${API}/wintouch/pending-movements-summary`);
              setPendingMovementsCount(pendingRes.data.pending_batches || 0);
              
              await loadWintouchBatches();
              
              const skuExcRes = await axios.get(`${API}/sku-exceptions/unresolved-count`);
              setUnresolvedCount(skuExcRes.data.count || 0);
            } catch (refreshErr) {
              console.error('Error refreshing data after processing:', refreshErr);
            }
            
          } else if (job.status === 'FAILED') {
            completed = true;
            setSageProcessingResult({
              success: false,
              error: 'processing_failed',
              message: job.error || 'Processamento falhou',
            });
            setShowSageResult(true);
          }
        } catch (pollErr) {
          console.error(`Poll error: ${pollErr.message}`);
          // Continue polling unless it's a fatal error
          if (pollErr.response?.status === 404) {
            completed = true;
            setSageProcessingResult({
              success: false,
              error: 'job_not_found',
              message: 'Job de processamento não encontrado',
            });
            setShowSageResult(true);
          }
        }
      }
      
      // Timeout reached
      if (!completed) {
        setSageProcessingResult({
          success: false,
          error: 'timeout',
          message: 'Processamento excedeu o tempo limite. Verifique o estado manualmente.',
        });
        setShowSageResult(true);
      }
      
    } catch (err) {
      console.error('SAGE processing failed:', err);
      setSageProcessingResult({
        success: false,
        error: err.response?.data?.detail || err.message || 'Erro no processamento SAGE',
      });
      setShowSageResult(true);
    } finally {
      setSageProcessingLoading(false);
    }
  };
  
  // Handle SAGE Processing Preview (Dry-Run) - READ-ONLY
  // UNIFIED WORKFLOW: Stage 1 (Ingestion) + Preview (no SAGE writes)
  const handleSagePreview = async () => {
    setPreviewLoading(true);
    setPreviewResult(null);
    setProcessResultMode(false);
    setProcessResult(null);
    setShowConfirmation(false);
    setUnifiedIngestionResult(null);
    setUnifiedWorkflowStep('ingesting');
    
    try {
      const token = localStorage.getItem('token');
      
      // STEP 1: Execute Stage 1 ingestion (XML → MongoDB, NO SAGE writes)
      let ingestionData = null;
      try {
        const ingestionRes = await axios.post(`${API}/onedrive/run-ingestion`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        ingestionData = ingestionRes.data;
        
        // Extract ingestion summary
        const summary = ingestionData.summary || {};
        const fileResults = ingestionData.file_results || [];
        const aggregated = ingestionData.aggregated || {};
        
        const newlyIngested = fileResults.filter(f => 
          f.status === 'SUCCESS' || f.status === 'INGESTED'
        ).length;
        const alreadyProcessed = summary.files_already_processed || 
          fileResults.filter(f => f.status === 'ALREADY_PROCESSED').length;
        const failed = fileResults.filter(f => 
          f.status === 'FAILED' || f.status === 'ERROR'
        ).length;
        
        setUnifiedIngestionResult({
          success: ingestionData.success,
          filesDiscovered: summary.files_discovered || fileResults.length,
          newlyIngested,
          alreadyProcessed,
          failed,
          batchesCreated: aggregated.batches_created || 0,
          skuExceptions: aggregated.sku_exceptions_created || 0,
          hasNewContent: newlyIngested > 0 || (aggregated.batches_created || 0) > 0,
        });
        
        // NOTE: Ingestion failure is non-blocking - we continue to show existing pending movements
        
      } catch (ingestionErr) {
        console.error('Stage 1 ingestion failed:', ingestionErr);
        // Non-blocking: continue to preview even if ingestion fails
        // This allows viewing existing pending movements
        setUnifiedIngestionResult({
          success: false,
          error: ingestionErr.response?.data?.detail || ingestionErr.message || 'Erro na ingestão',
          filesDiscovered: 0,
          newlyIngested: 0,
          alreadyProcessed: 0,
          failed: 0,
          batchesCreated: 0,
          skuExceptions: 0,
          hasNewContent: false,
        });
      }
      
      // STEP 2: Load COMPLETE preview - both WinTouch batches AND Stock Issues
      setUnifiedWorkflowStep('previewing');
      
      // 2a. Get pending movements summary (WinTouch batches + Stock Issues counts)
      const summaryRes = await axios.get(`${API}/wintouch/pending-movements-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pendingSummary = summaryRes.data;
      
      // 2b. Get Stock Issues dry-run preview (detailed Stock Issues info)
      let stockIssuesPreview = null;
      try {
        const stockIssuesRes = await axios.get(`${API}/stock-issues/dry-run/preview`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        stockIssuesPreview = stockIssuesRes.data;
      } catch (stockErr) {
        console.warn('Stock issues preview failed:', stockErr);
        stockIssuesPreview = { issues_processed: 0, issues_blocked: 0, documents: [] };
      }
      
      // 2c. Combine into unified preview result
      const combinedPreview = {
        // WinTouch Batches (normal daily movements)
        pending_batches: pendingSummary.pending_batches || 0,
        batch_out_units: pendingSummary.out_units || 0,
        batch_in_units: pendingSummary.in_units || 0,
        stores: pendingSummary.stores || [],
        date_range: pendingSummary.date_range,
        
        // Stock Issues (complementary movements)
        ready_stock_issues: pendingSummary.ready_stock_issues || 0,
        issues_processed: stockIssuesPreview?.issues_processed || 0,
        issues_blocked: stockIssuesPreview?.issues_blocked || 0,
        blocked_issues: stockIssuesPreview?.blocked_issues || [],
        stock_issue_documents: stockIssuesPreview?.documents || [],
        
        // Combined totals
        total_processable: pendingSummary.total_processable || 0,
        total_units: (pendingSummary.out_units || 0) + (pendingSummary.in_units || 0),
        
        // Estimated documents to create
        // Each pending batch creates 1 ISL (if has isl_lines) + 1 IEL (if has iel_lines)
        // Stock issues create complementary documents (aggregated by month)
        isl_created: (pendingSummary.pending_batches || 0) + (stockIssuesPreview?.isl_created || 0),
        iel_created: (stockIssuesPreview?.iel_created || 0),
        total_units_posted: (pendingSummary.out_units || 0) + (pendingSummary.in_units || 0) + (stockIssuesPreview?.total_units_posted || 0),
        
        // Original stock issues preview data for detailed view
        documents: stockIssuesPreview?.documents || [],
        
        // Status flags
        is_processing: pendingSummary.is_processing || false,
        unknown_skus: pendingSummary.unknown_skus || 0,
      };
      
      setPreviewResult(combinedPreview);
      setUnifiedWorkflowStep('ready');
      setShowPreviewModal(true);
      
      // Update counts in header
      setPendingMovementsCount(pendingSummary.pending_batches || 0);
      setTotalProcessableCount(pendingSummary.total_processable || 0);
      
      // Refresh other counts if ingestion added new content
      if (ingestionData?.success && ingestionData?.aggregated?.sku_exceptions_created > 0) {
        const count = await fetchUnresolvedCount();
        setUnresolvedCount(count);
        
        const issuesCount = await fetchStockIssuesCount();
        setStockIssuesCount(issuesCount);
      }
      
    } catch (err) {
      console.error('Preview failed:', err);
      setUnifiedWorkflowStep('error');
      const errorDetail = err.response?.data?.detail || err.message || 'Erro ao gerar pré-visualização';
      setPreviewResult({
        success: false,
        error: errorDetail,
      });
      setShowPreviewModal(true);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Handle confirmation step - show confirmation dialog
  const handleShowConfirmation = () => {
    setShowConfirmation(true);
  };
  
  // Handle cancel confirmation
  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };
  
  // Handle actual SAGE processing from Preview (with stale-plan validation)
  // ASYNC JOB WORKFLOW: Start job + Poll for progress
  const handleProcessFromPreview = async () => {
    setShowConfirmation(false);
    setIsProcessingFromPreview(true);
    setUnifiedWorkflowStep('processing');
    
    try {
      const token = localStorage.getItem('token');
      
      // STEP 1: Start async processing job
      const startRes = await axios.post(`${API}/wintouch/process-pending-movements/start`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!startRes.data.success) {
        setProcessResult({
          success: false,
          error: startRes.data.error || 'start_failed',
          message: startRes.data.message || 'Erro ao iniciar processamento',
        });
        setProcessResultMode(true);
        setUnifiedWorkflowStep('error');
        return;
      }
      
      const jobId = startRes.data.job_id;
      
      // STEP 2: Poll for job status
      let completed = false;
      let pollCount = 0;
      const maxPolls = 180; // 15 minutes max (5s intervals)
      
      while (!completed && pollCount < maxPolls) {
        await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
        pollCount++;
        
        try {
          const statusRes = await axios.get(`${API}/wintouch/process-pending-movements/status/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const job = statusRes.data;
          
          // Update progress in UI
          setUnifiedWorkflowStep(job.status.toLowerCase());
          if (job.progress) {
            // Could update a progress bar here
            console.log(`Job ${jobId}: ${job.progress.message} (${job.progress.percent}%)`);
          }
          
          if (job.status === 'COMPLETED') {
            completed = true;
            setProcessResult(job.result);
            setProcessResultMode(true);
            setUnifiedWorkflowStep('done');
            
            // Refresh ALL related data after processing
            try {
              // Refresh stock issues count
              const stockCount = await fetchStockIssuesCount();
              setStockIssuesCount(stockCount);
              
              // Refresh pending movements count
              const pendingRes = await axios.get(`${API}/wintouch/pending-movements-summary`);
              setPendingMovementsCount(pendingRes.data.pending_batches || 0);
              setTotalProcessableCount(pendingRes.data.total_processable || 0);
              
              // Refresh WinTouch batches table (ISL/IEL references)
              await loadWintouchBatches();
              
              // Refresh SKU exceptions count (unresolved)
              const skuExcRes = await axios.get(`${API}/sku-exceptions/unresolved-count`);
              setUnresolvedCount(skuExcRes.data.count || 0);
              
              console.log('All data refreshed after Stage 2 processing');
            } catch (refreshErr) {
              console.error('Error refreshing data after processing:', refreshErr);
              // Don't fail the whole operation if refresh fails
            }
            
          } else if (job.status === 'FAILED') {
            completed = true;
            setProcessResult({
              success: false,
              error: 'processing_failed',
              message: job.error || 'Processamento falhou',
            });
            setProcessResultMode(true);
            setUnifiedWorkflowStep('error');
          }
          
        } catch (pollErr) {
          console.warn(`Poll ${pollCount} failed:`, pollErr);
          // Continue polling - might be temporary network issue
        }
      }
      
      if (!completed) {
        // Timeout - job is still running but we've waited too long
        setProcessResult({
          success: false,
          error: 'poll_timeout',
          message: 'O processamento está a demorar mais do que esperado. O job continua em execução no servidor. Atualize a página para verificar o resultado.',
          job_id: jobId,
        });
        setProcessResultMode(true);
        setUnifiedWorkflowStep('error');
      }
      
    } catch (err) {
      console.error('Processing from preview failed:', err);
      
      // Check if it's a proxy timeout error
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || '';
      const isProxyTimeout = errorMessage.toLowerCase().includes('timeout') || 
                            errorMessage.toLowerCase().includes('proxy') ||
                            err.code === 'ECONNABORTED';
      
      if (isProxyTimeout) {
        setProcessResult({
          success: false,
          error: 'proxy_timeout',
          message: 'O processamento pode ter continuado no servidor. Aguarde alguns minutos e atualize a página para verificar o resultado.',
        });
      } else {
        setProcessResult({
          success: false,
          error: err.response?.data?.error || 'processing_failed',
          message: errorMessage || 'Erro no processamento SAGE',
        });
      }
      setProcessResultMode(true);
      setUnifiedWorkflowStep('error');
    } finally {
      setIsProcessingFromPreview(false);
    }
  };
  
  // Handle closing the preview modal - refresh data if processing was done
  const handleClosePreviewModal = async () => {
    const wasProcessed = processResultMode && processResult?.success;
    
    setShowPreviewModal(false);
    setProcessResultMode(false);
    setProcessResult(null);
    setPreviewResult(null);
    setShowConfirmation(false);
    
    // Refresh data if processing was successful
    if (wasProcessed) {
      const stockCount = await fetchStockIssuesCount();
      setStockIssuesCount(stockCount);
      
      const pendingRes = await axios.get(`${API}/wintouch/pending-movements-summary`);
      setPendingMovementsCount(pendingRes.data.pending_batches || 0);
      setTotalProcessableCount(pendingRes.data.total_processable || 0);
      
      // Refresh audit log if on that tab
      if (activeTab === 'audit-log') {
        loadAuditLog();
      }
      
      // Refresh WinTouch batches table (Importações XML → SAGE)
      if (activeTab === 'wintouch-batches') {
        await loadWintouchBatches();
      }
    }
  };
  
  // Calculate if processing is available from preview
  const canProcessFromPreview = previewResult && !previewResult.error && (
    (previewResult.issues_processed || 0) > 0 ||
    (previewResult.documents?.length || 0) > 0 ||
    totalProcessableCount > 0
  );
  
  // Handle Retail Sales Sync (backfill/recovery)
  const handleRetailSync = async () => {
    setRetailSyncLoading(true);
    setRetailSyncResult(null);
    
    try {
      const res = await axios.post(`${API}/onedrive/sync-retail-analytics`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setRetailSyncResult({
        success: res.data.success,
        filesProcessed: res.data.files_processed || 0,
        documentsImported: res.data.documents_imported || 0,
        documentsSkipped: res.data.documents_skipped || 0,
        linesImported: res.data.lines_imported || 0,
        paymentsImported: res.data.payments_imported || 0,
        errors: res.data.errors || [],
      });
      setShowRetailSyncResult(true);
    } catch (err) {
      console.error('Retail sync failed:', err);
      setRetailSyncResult({
        success: false,
        error: err.response?.data?.detail || err.message || 'Erro ao sincronizar dados de vendas',
      });
      setShowRetailSyncResult(true);
    } finally {
      setRetailSyncLoading(false);
    }
  };
  
  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Auditoria Lojas</h1>
          <p className="text-sm text-slate-500 mt-1">
            Histórico de alterações e artigos por resolver
          </p>
        </div>
        
        {/* OneDrive Manual Check Button */}
        <div className="flex gap-2 flex-wrap">
          {canRunOneDriveCheck && (
            <Button 
              onClick={handleOneDriveCheck}
              disabled={oneDriveLoading}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="onedrive-check-btn"
            >
              {oneDriveLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Cloud className="w-4 h-4" />
              )}
              {oneDriveLoading ? 'A verificar...' : 'Verificar OneDrive agora'}
            </Button>
          )}
          
          {/* XML Ingestion Button - Admin Only */}
          {canRunIngestion && (
            <Button 
              onClick={handleOpenIngestionConfirm}
              disabled={ingestionLoading}
              variant="default"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              data-testid="xml-ingestion-btn"
            >
              {ingestionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              {ingestionLoading ? 'A ingerir...' : 'Executar Ingestão XML'}
            </Button>
          )}
          
          {canRunSageProcessing && (
            <Button 
              onClick={handleSagePreview}
              disabled={previewLoading || sageProcessingLoading}
              variant="default"
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="sage-processing-btn"
            >
              {previewLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Package className="w-4 h-4" />
              )}
              {previewLoading 
                ? (unifiedWorkflowStep === 'ingesting' ? 'A verificar XML...' : 'A calcular...')
                : 'Processar movimentos SAGE'}
              {totalProcessableCount > 0 && !previewLoading && !sageProcessingLoading && (
                <Badge variant="secondary" className="ml-1 bg-white text-emerald-700">
                  {totalProcessableCount}
                </Badge>
              )}
            </Button>
          )}
          
          {/* SAGE Processing Preview - Admin Only (hidden, same as above but explicit preview) */}
          {/* The main button already opens the preview, so this can be removed or made secondary */}
          
          {/* Additional Actions Menu */}
          {canRunOneDriveCheck && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="more-actions-btn">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={handleRetailSync}
                  disabled={retailSyncLoading}
                  className="flex items-center gap-2 cursor-pointer"
                  data-testid="retail-sync-btn"
                >
                  {retailSyncLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ShoppingCart className="w-4 h-4" />
                  )}
                  <div className="flex flex-col">
                    <span>{retailSyncLoading ? 'A sincronizar...' : 'Sincronizar dados de vendas'}</span>
                    <span className="text-xs text-slate-500">Atualiza Dashboard Vendas sem alterar stocks</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      
      {/* OneDrive Result Dialog */}
      <Dialog open={showOneDriveResult} onOpenChange={setShowOneDriveResult}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {oneDriveResult?.success ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              Verificação OneDrive
            </DialogTitle>
          </DialogHeader>
          
          {oneDriveResult && (
            <div className="space-y-4">
              {oneDriveResult.error ? (
                <div className="p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                  {oneDriveResult.error}
                </div>
              ) : (
                <>
                  {oneDriveResult.filesDiscovered === 0 ? (
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <Cloud className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-slate-600 font-medium">
                        Nenhum novo ficheiro WinTouch encontrado.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Ficheiros encontrados</span>
                        <span className="font-medium">{oneDriveResult.filesDiscovered}</span>
                      </div>
                      {oneDriveResult.newlyIngested > 0 && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-emerald-600">Novos processados</span>
                          <span className="font-medium text-emerald-600">{oneDriveResult.newlyIngested}</span>
                        </div>
                      )}
                      {oneDriveResult.alreadyProcessed > 0 && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-slate-500">Já processados</span>
                          <span className="font-medium">{oneDriveResult.alreadyProcessed}</span>
                        </div>
                      )}
                      {oneDriveResult.skippedCutoff > 0 && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-amber-600">Ignorados (cutoff)</span>
                          <span className="font-medium text-amber-600">{oneDriveResult.skippedCutoff}</span>
                        </div>
                      )}
                      {oneDriveResult.failed > 0 && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-red-600">Com erro</span>
                          <span className="font-medium text-red-600">{oneDriveResult.failed}</span>
                        </div>
                      )}
                      {oneDriveResult.unknownSkus > 0 && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-orange-600">Artigos por resolver</span>
                          <span className="font-medium text-orange-600">{oneDriveResult.unknownSkus}</span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              
              <Button 
                onClick={() => setShowOneDriveResult(false)} 
                className="w-full"
                variant="outline"
              >
                Fechar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* XML Ingestion Confirmation Modal */}
      <Dialog open={showIngestionConfirm} onOpenChange={setShowIngestionConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Executar Ingestão XML
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg space-y-2">
              <p className="text-sm text-blue-800 font-medium">
                Esta operação irá:
              </p>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li>Processar ficheiros XML novos do OneDrive</li>
                <li>Classificar SKUs (Físico/Serviço/Financeiro)</li>
                <li>Criar batches e documentos no StockFlow</li>
                <li>Identificar exceções de SKU (artigos desconhecidos)</li>
              </ul>
            </div>
            
            <div className="p-4 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Nota:</strong> Esta operação executa apenas Stage 1 (XML → StockFlow).
                Não serão criados documentos ISL/IEL no SAGE.
              </p>
            </div>
            
            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              <p>Ficheiros já processados serão ignorados (idempotência por hash).</p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                onClick={() => setShowIngestionConfirm(false)}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleExecuteIngestion}
                disabled={ingestionLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-ingestion-btn"
              >
                {ingestionLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    A executar...
                  </>
                ) : (
                  'Executar Ingestão'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* XML Ingestion Result Modal */}
      <Dialog open={showIngestionResult} onOpenChange={setShowIngestionResult}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {ingestionResult?.success ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              Resultado da Ingestão XML
            </DialogTitle>
          </DialogHeader>
          
          {ingestionResult && (
            <div className="space-y-4">
              {/* Progress indicator when job is running */}
              {ingestionResult.inProgress ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-slate-700">A processar ficheiros XML...</p>
                    {ingestionResult.progress && (
                      <>
                        <p className="text-xs text-slate-500 mt-1">
                          {ingestionResult.progress.files_processed || 0} de {ingestionResult.progress.files_discovered || 0} ficheiros
                        </p>
                        <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${ingestionResult.progress.percent || 0}%` }}
                          />
                        </div>
                        <p className="text-xs text-slate-400 mt-2 truncate px-4">
                          {ingestionResult.progress.message || ''}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              ) : ingestionResult.error ? (
                <div className="p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                  {ingestionResult.error}
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-slate-700">{ingestionResult.filesDiscovered}</p>
                      <p className="text-xs text-slate-500">Ficheiros encontrados</p>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-emerald-600">{ingestionResult.newlyIngested}</p>
                      <p className="text-xs text-emerald-600">Novos processados</p>
                    </div>
                  </div>
                  
                  {/* Details */}
                  <div className="space-y-2 text-sm">
                    {ingestionResult.alreadyProcessed > 0 && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-500">Já processados (ignorados)</span>
                        <span className="font-medium">{ingestionResult.alreadyProcessed}</span>
                      </div>
                    )}
                    {ingestionResult.batchesCreated > 0 && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-blue-600">Batches criados</span>
                        <span className="font-medium text-blue-600">{ingestionResult.batchesCreated}</span>
                      </div>
                    )}
                    {ingestionResult.documentsCreated > 0 && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Documentos criados</span>
                        <span className="font-medium">{ingestionResult.documentsCreated}</span>
                      </div>
                    )}
                    {(ingestionResult.physicalLines > 0 || ingestionResult.serviceLines > 0 || ingestionResult.financialLines > 0) && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Linhas classificadas</span>
                        <span className="font-medium">
                          {ingestionResult.physicalLines} físicas, {ingestionResult.serviceLines} serviços, {ingestionResult.financialLines} financeiras
                        </span>
                      </div>
                    )}
                    {ingestionResult.skuExceptions > 0 && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-orange-600">Exceções de SKU</span>
                        <span className="font-medium text-orange-600">{ingestionResult.skuExceptions}</span>
                      </div>
                    )}
                    {ingestionResult.reclassified > 0 && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-blue-600">Batches reclassificados</span>
                        <span className="font-medium text-blue-600">{ingestionResult.reclassified}</span>
                      </div>
                    )}
                    {ingestionResult.failed > 0 && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-red-600">Ficheiros com erro</span>
                        <span className="font-medium text-red-600">{ingestionResult.failed}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Reclassification info */}
                  {ingestionResult.reclassifiedInfo && ingestionResult.reclassifiedInfo.length > 0 && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-700 mb-2">Batches reclassificados:</p>
                      <ul className="text-xs text-blue-600 space-y-1">
                        {ingestionResult.reclassifiedInfo.slice(0, 5).map((info, idx) => (
                          <li key={idx}>
                            <span className="font-medium">{info.filename || info.batchId}</span>: {' '}
                            {info.status === 'RECLASSIFIED_RESOLVED' ? (
                              <span className="text-emerald-600">Resolvido - agora pronto para SAGE</span>
                            ) : (
                              <span className="text-amber-600">Ainda com exceções pendentes</span>
                            )}
                          </li>
                        ))}
                        {ingestionResult.reclassifiedInfo.length > 5 && (
                          <li className="text-blue-500">... e mais {ingestionResult.reclassifiedInfo.length - 5} batches</li>
                        )}
                      </ul>
                    </div>
                  )}
                  
                  {/* Errors list */}
                  {ingestionResult.errors && ingestionResult.errors.length > 0 && (
                    <div className="p-3 bg-red-50 rounded-lg">
                      <p className="text-sm font-medium text-red-700 mb-2">Erros encontrados:</p>
                      <ul className="text-xs text-red-600 space-y-1">
                        {ingestionResult.errors.slice(0, 5).map((err, idx) => (
                          <li key={idx}>
                            <span className="font-medium">{err.filename}</span>: {err.error}
                          </li>
                        ))}
                        {ingestionResult.errors.length > 5 && (
                          <li className="text-red-500">... e mais {ingestionResult.errors.length - 5} erros</li>
                        )}
                      </ul>
                    </div>
                  )}
                  
                  {/* Success note */}
                  {ingestionResult.newlyIngested > 0 && (
                    <div className="p-3 bg-emerald-50 rounded-lg text-sm text-emerald-700">
                      <p>
                        <strong>Próximo passo:</strong> Os novos batches estão agora disponíveis para 
                        pré-visualização e processamento SAGE (Stage 2).
                      </p>
                    </div>
                  )}
                </>
              )}
              
              <Button 
                onClick={() => setShowIngestionResult(false)} 
                className="w-full"
                variant="outline"
              >
                Fechar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* SAGE Confirmation Modal */}
      <Dialog open={showSageConfirmModal} onOpenChange={setShowSageConfirmModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              Processar movimentos pendentes no SAGE
            </DialogTitle>
          </DialogHeader>
          
          {sagePendingSummary && (
            <div className="space-y-4">
              {sagePendingSummary.error ? (
                <div className="p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                  {sagePendingSummary.error}
                </div>
              ) : sagePendingSummary.is_processing ? (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="font-medium">Processamento já em curso</span>
                  </div>
                  <p className="text-amber-600 text-sm mt-1">
                    Iniciado por: {sagePendingSummary.processing_user}
                  </p>
                </div>
              ) : sagePendingSummary.pending_batches === 0 ? (
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-slate-600 font-medium">
                    Não existem movimentos pendentes para processar.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2 bg-slate-50 p-4 rounded-lg">
                    <div className="flex justify-between py-1">
                      <span className="text-slate-600">Batches pendentes</span>
                      <span className="font-semibold">{sagePendingSummary.pending_batches}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-600">Lojas</span>
                      <span className="font-medium">{sagePendingSummary.stores?.join(', ') || '—'}</span>
                    </div>
                    {sagePendingSummary.date_range?.min && (
                      <div className="flex justify-between py-1">
                        <span className="text-slate-600">Período</span>
                        <span className="font-medium">
                          {sagePendingSummary.date_range.min} — {sagePendingSummary.date_range.max}
                        </span>
                      </div>
                    )}
                    <div className="border-t my-2"></div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-600">Movimentos OUT pendentes</span>
                      <span className="font-semibold text-red-600">{sagePendingSummary.out_units} un.</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-600">Movimentos IN pendentes</span>
                      <span className="font-semibold text-emerald-600">{sagePendingSummary.in_units} un.</span>
                    </div>
                    {sagePendingSummary.unknown_skus > 0 && (
                      <div className="flex justify-between py-1">
                        <span className="text-amber-600">Artigos por resolver</span>
                        <span className="font-semibold text-amber-600">{sagePendingSummary.unknown_skus}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-3 bg-amber-50 rounded-lg text-amber-700 text-sm">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Esta ação irá criar documentos reais no SAGE. Confirma que deseja prosseguir?
                  </div>
                </>
              )}
              
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowSageConfirmModal(false)} 
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
                {sagePendingSummary.pending_batches > 0 && !sagePendingSummary.is_processing && (
                  <Button 
                    onClick={handleSageProcessing}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Processar no SAGE
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* SAGE Processing Result Modal */}
      <Dialog open={showSageResult} onOpenChange={setShowSageResult}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {sageProcessingResult?.success ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : sageProcessingResult?.error ? (
                <XCircle className="w-5 h-5 text-red-500" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              )}
              {sageProcessingResult?.success 
                ? 'Processamento concluído' 
                : sageProcessingResult?.errors?.length > 0
                  ? 'Processamento concluído com pendências'
                  : 'Erro no processamento'
              }
            </DialogTitle>
          </DialogHeader>
          
          {sageProcessingResult && (
            <div className="space-y-4">
              {sageProcessingResult.error ? (
                <div className="p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                  {sageProcessingResult.error}
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Batches processados</span>
                      <span className="font-medium">{sageProcessingResult.batches_processed}</span>
                    </div>
                    
                    {(sageProcessingResult.isl_created > 0 || sageProcessingResult.isl_reconciled > 0) && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">ISL criados</span>
                        <span className="font-medium text-emerald-600">
                          {sageProcessingResult.isl_created}
                          {sageProcessingResult.isl_reconciled > 0 && (
                            <span className="text-slate-400 text-sm ml-1">
                              (+{sageProcessingResult.isl_reconciled} reconciliados)
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    
                    {(sageProcessingResult.iel_created > 0 || sageProcessingResult.iel_reconciled > 0) && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">IEL criados</span>
                        <span className="font-medium text-emerald-600">
                          {sageProcessingResult.iel_created}
                          {sageProcessingResult.iel_reconciled > 0 && (
                            <span className="text-slate-400 text-sm ml-1">
                              (+{sageProcessingResult.iel_reconciled} reconciliados)
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Unidades OUT lançadas</span>
                      <span className="font-medium">{sageProcessingResult.out_posted}</span>
                    </div>
                    
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Unidades IN lançadas</span>
                      <span className="font-medium">{sageProcessingResult.in_posted}</span>
                    </div>
                    
                    {sageProcessingResult.stock_issues_count > 0 && (
                      <div className="flex justify-between py-2 border-b bg-amber-50 -mx-4 px-4">
                        <span className="text-amber-700 font-medium">Stocks por resolver</span>
                        <span className="font-semibold text-amber-700">
                          {sageProcessingResult.stock_issues_count} artigos / {sageProcessingResult.stock_issues_units} un.
                        </span>
                      </div>
                    )}
                    
                    {sageProcessingResult.unknown_skus > 0 && (
                      <div className="flex justify-between py-2 border-b bg-orange-50 -mx-4 px-4">
                        <span className="text-orange-700">Artigos por resolver</span>
                        <span className="font-semibold text-orange-700">{sageProcessingResult.unknown_skus}</span>
                      </div>
                    )}
                    
                    {sageProcessingResult.errors?.length > 0 && (
                      <div className="p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                        <span className="font-medium">Erros técnicos: {sageProcessingResult.errors.length}</span>
                        <ul className="mt-1 text-xs list-disc list-inside">
                          {sageProcessingResult.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>{err.batch_id}: {err.error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
              
              <Button 
                onClick={() => setShowSageResult(false)} 
                className="w-full"
                variant="outline"
              >
                Fechar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Retail Sales Sync Result Dialog */}
      <Dialog open={showRetailSyncResult} onOpenChange={setShowRetailSyncResult}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {retailSyncResult?.success ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              Sincronização de Vendas
            </DialogTitle>
          </DialogHeader>
          
          {retailSyncResult && (
            <div className="space-y-4">
              {retailSyncResult.error ? (
                <div className="p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                  {retailSyncResult.error}
                </div>
              ) : (
                <>
                  <p className="text-slate-600 text-sm">
                    Reprocessados os ficheiros WinTouch para atualizar vendas, pagamentos e devoluções.
                    <br />
                    <span className="text-slate-500">(Não altera stocks no SAGE)</span>
                  </p>
                  
                  {retailSyncResult.documentsImported === 0 && retailSyncResult.filesProcessed > 0 ? (
                    <div className="p-3 bg-slate-50 rounded-lg text-slate-600 text-sm">
                      Nenhum novo dado encontrado. Todos os documentos já estavam sincronizados.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Ficheiros analisados</span>
                        <span className="font-medium">{retailSyncResult.filesProcessed}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Documentos importados</span>
                        <span className="font-medium text-emerald-600">{retailSyncResult.documentsImported}</span>
                      </div>
                      {retailSyncResult.documentsSkipped > 0 && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-slate-500">Documentos já existentes</span>
                          <span className="text-slate-500">{retailSyncResult.documentsSkipped}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Linhas</span>
                        <span className="font-medium">{retailSyncResult.linesImported}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Pagamentos</span>
                        <span className="font-medium">{retailSyncResult.paymentsImported}</span>
                      </div>
                      {retailSyncResult.errors?.length > 0 && (
                        <div className="p-3 bg-amber-50 rounded-lg mt-2">
                          <span className="text-amber-700 text-sm font-medium">Erros ({retailSyncResult.errors.length}):</span>
                          <ul className="text-amber-600 text-xs mt-1 list-disc list-inside">
                            {retailSyncResult.errors.slice(0, 5).map((err, i) => (
                              <li key={i}>{err.file}: {err.error || JSON.stringify(err.errors)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              
              <Button 
                onClick={() => setShowRetailSyncResult(false)} 
                className="w-full"
                variant="outline"
              >
                Fechar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* SAGE Processing Preview Modal - With Confirmation and Processing */}
      <Dialog open={showPreviewModal} onOpenChange={handleClosePreviewModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {processResultMode ? (
                processResult?.success ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    Processamento Concluído
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 text-red-600" />
                    {processResult?.error === 'stale_plan' ? 'Plano Desatualizado' : 'Erro no Processamento'}
                  </>
                )
              ) : (
                <>
                  <Eye className="w-5 h-5 text-blue-600" />
                  Pré-visualização — Processamento SAGE
                </>
              )}
            </DialogTitle>
            {!processResultMode && (
              <div className="flex items-center gap-2 bg-blue-50 text-blue-700 text-xs px-3 py-1.5 rounded-full w-fit mt-2">
                <AlertTriangle className="w-3 h-3" />
                Simulação — Não cria documentos no SAGE
              </div>
            )}
          </DialogHeader>
          
          {/* UNIFIED INGESTION RESULT (Stage 1 summary) */}
          {!processResultMode && unifiedIngestionResult && (
            <div className={`rounded-lg p-3 text-sm mb-4 ${
              unifiedIngestionResult.success 
                ? unifiedIngestionResult.hasNewContent 
                  ? 'bg-emerald-50 border border-emerald-200'
                  : 'bg-slate-50 border border-slate-200'
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <FileText className={`w-4 h-4 ${
                  unifiedIngestionResult.success 
                    ? unifiedIngestionResult.hasNewContent ? 'text-emerald-600' : 'text-slate-500'
                    : 'text-amber-600'
                }`} />
                <span className="font-medium text-slate-700">
                  Ingestão XML (Stage 1)
                </span>
                {unifiedWorkflowStep === 'ingesting' && (
                  <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
                )}
              </div>
              
              {unifiedIngestionResult.error ? (
                <p className="text-amber-700 text-xs">{unifiedIngestionResult.error}</p>
              ) : (
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div>
                    <span className="text-slate-500">Ficheiros:</span>
                    <span className="ml-1 font-medium">{unifiedIngestionResult.filesDiscovered}</span>
                  </div>
                  <div>
                    <span className={unifiedIngestionResult.newlyIngested > 0 ? 'text-emerald-600' : 'text-slate-500'}>
                      Novos:
                    </span>
                    <span className={`ml-1 font-medium ${unifiedIngestionResult.newlyIngested > 0 ? 'text-emerald-600' : ''}`}>
                      {unifiedIngestionResult.newlyIngested}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Ignorados:</span>
                    <span className="ml-1 font-medium">{unifiedIngestionResult.alreadyProcessed}</span>
                  </div>
                  {unifiedIngestionResult.batchesCreated > 0 && (
                    <div>
                      <span className="text-emerald-600">Batches:</span>
                      <span className="ml-1 font-medium text-emerald-600">+{unifiedIngestionResult.batchesCreated}</span>
                    </div>
                  )}
                  {unifiedIngestionResult.skuExceptions > 0 && (
                    <div>
                      <span className="text-orange-600">Exceções SKU:</span>
                      <span className="ml-1 font-medium text-orange-600">{unifiedIngestionResult.skuExceptions}</span>
                    </div>
                  )}
                  {unifiedIngestionResult.failed > 0 && (
                    <div>
                      <span className="text-red-600">Erros:</span>
                      <span className="ml-1 font-medium text-red-600">{unifiedIngestionResult.failed}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* CONFIRMATION DIALOG (overlay within modal) */}
          {showConfirmation && previewResult && !processResultMode && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="confirmation-overlay">
              <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl" data-testid="confirmation-dialog">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Confirmar processamento SAGE
                </h3>
                <div className="space-y-3 text-sm text-slate-700">
                  <p>
                    Serão processados <strong>{previewResult.total_processable || ((previewResult.pending_batches || 0) + (previewResult.ready_stock_issues || 0))} elementos</strong> com <strong>{previewResult.total_units_posted || previewResult.total_units || 0} unidades</strong>.
                  </p>
                  
                  {/* Breakdown summary */}
                  <div className="bg-slate-50 rounded p-3 space-y-1.5 text-xs">
                    {(previewResult.pending_batches || 0) > 0 && (
                      <div className="flex justify-between">
                        <span>Batches WinTouch:</span>
                        <span className="font-semibold">{previewResult.pending_batches} ({previewResult.batch_out_units || 0} OUT + {previewResult.batch_in_units || 0} IN)</span>
                      </div>
                    )}
                    {((previewResult.ready_stock_issues || 0) > 0 || (previewResult.issues_processed || 0) > 0) && (
                      <div className="flex justify-between">
                        <span>Stock Issues:</span>
                        <span className="font-semibold">{previewResult.issues_processed || previewResult.ready_stock_issues || 0} elegíveis</span>
                      </div>
                    )}
                    <div className="border-t pt-1 mt-1 flex justify-between font-medium">
                      <span>Documentos SAGE estimados:</span>
                      <span>{(previewResult.isl_created || previewResult.pending_batches || 0) + (previewResult.iel_created || 0)} (ISL + IEL)</span>
                    </div>
                  </div>
                  
                  {previewResult.documents?.length > 0 && (
                    <div className="bg-purple-50 rounded p-3 space-y-1.5">
                      <div className="text-xs font-medium text-purple-700 mb-1">Stock Issues Complementares:</div>
                      {previewResult.documents.map((doc, idx) => (
                        <div key={idx} className="text-xs">
                          <div className="font-medium">{doc.document_type || `${doc.movement_type} Complementar`}</div>
                          <div className="text-slate-500">
                            {getStoreName(doc.store_code)} • Data: {doc.document_date?.split('-').reverse().join('/') || '—'} • Warehouse: {doc.warehouse || '012'}
                          </div>
                          <div className="font-mono text-slate-600">
                            VossoNumero: {doc.vosso_numero}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-amber-700 bg-amber-50 p-2 rounded text-xs">
                    Esta operação irá criar documentos reais no SAGE.
                  </p>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button 
                    onClick={handleCancelConfirmation}
                    variant="outline"
                    className="flex-1"
                    data-testid="cancel-confirmation-btn"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleProcessFromPreview}
                    disabled={isProcessingFromPreview}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="confirm-process-btn"
                  >
                    {isProcessingFromPreview ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        A processar no SAGE…
                      </>
                    ) : (
                      'Confirmar e criar no SAGE'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* PROCESSING RESULT VIEW */}
          {processResultMode && processResult && (
            <div className="space-y-6">
              {/* Determine actual success: must have created documents if we expected to create them */}
              {(() => {
                const plannedDocs = previewResult?.documents?.length || 0;
                const createdDocs = (processResult.isl_created || 0) + (processResult.iel_created || 0) + 
                                   (processResult.complementary_isl_created || 0) + (processResult.complementary_iel_created || 0);
                const isActualSuccess = processResult.success && (plannedDocs === 0 || createdDocs > 0);
                const isNoOp = processResult.success && plannedDocs > 0 && createdDocs === 0;
                
                if (isActualSuccess && !isNoOp) {
                  return (
                    <>
                      {/* Success Summary */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-emerald-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-emerald-700">
                            {processResult.isl_created || 0}
                          </div>
                          <div className="text-xs text-emerald-600">ISL Criadas</div>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-blue-700">
                            {processResult.iel_created || 0}
                          </div>
                          <div className="text-xs text-blue-600">IEL Criadas</div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-slate-700">
                            {(processResult.out_posted || 0) + (processResult.in_posted || 0)}
                          </div>
                          <div className="text-xs text-slate-500">Unidades Processadas</div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-purple-700">
                            {processResult.stock_issues_processed || 0}
                          </div>
                          <div className="text-xs text-purple-600">Stock Issues Resolvidos</div>
                        </div>
                      </div>
                      
                      {/* Complementary Documents Created */}
                      {processResult.complementary_documents?.length > 0 && (
                        <div className="space-y-3">
                          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Documentos SAGE Criados
                          </h3>
                          {processResult.complementary_documents.map((doc, idx) => (
                            <div key={idx} className="border rounded-lg overflow-hidden bg-emerald-50 border-emerald-200">
                              <div className="px-4 py-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge className="bg-emerald-600 text-white">
                                    {doc.document_type || `${doc.movement_type} Complementar`}
                                  </Badge>
                                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                  <div>
                                    <span className="text-slate-500 text-xs">Documento SAGE:</span>
                                    <div className="font-mono font-medium text-emerald-800">
                                      {doc.sage_reference || `${doc.sage_type}/${doc.sage_series}/${doc.sage_number}`}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="text-slate-500 text-xs">Data:</span>
                                    <div className="font-medium">
                                      {doc.document_date?.split('-').reverse().join('/') || '—'}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="text-slate-500 text-xs">Warehouse:</span>
                                    <div className="font-medium">{doc.warehouse || '012'}</div>
                                  </div>
                                  <div>
                                    <span className="text-slate-500 text-xs">VossoNumero:</span>
                                    <code className="px-2 py-0.5 bg-emerald-100 rounded font-mono text-xs">
                                      {doc.vosso_numero}
                                    </code>
                                  </div>
                                  <div>
                                    <span className="text-slate-500 text-xs">Loja:</span>
                                    <div className="font-medium">{getStoreName(doc.store_code)}</div>
                                  </div>
                                  <div>
                                    <span className="text-slate-500 text-xs">Unidades:</span>
                                    <div className="font-medium">{doc.units_posted || 0}</div>
                                  </div>
                                </div>
                                
                                {/* Lines */}
                                {doc.lines?.length > 0 && (
                                  <div className="mt-3 pt-2 border-t border-emerald-200">
                                    <div className="text-xs font-medium text-slate-500 mb-1">Linhas:</div>
                                    <div className="flex flex-wrap gap-2">
                                      {doc.lines.map((line, lineIdx) => (
                                        <span key={lineIdx} className="px-2 py-1 bg-white rounded text-xs">
                                          <code className="text-emerald-700">{line.effective_sage_sku}</code> ×{line.quantity}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Success message */}
                      <div className="p-4 bg-emerald-50 rounded-lg text-emerald-700 text-center">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                        <p className="font-medium">Processamento concluído com sucesso</p>
                        <p className="text-sm text-emerald-600 mt-1">
                          Os documentos foram criados no SAGE.
                        </p>
                      </div>
                    </>
                  );
                } else if (isNoOp) {
                  // NO-OP: Backend said success but no documents were created when we expected some
                  return (
                    <>
                      <div className="p-6 bg-amber-50 rounded-lg text-center">
                        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                        <p className="font-medium text-amber-800">Nenhum documento foi criado no SAGE</p>
                        <p className="text-sm text-amber-600 mt-2">
                          {processResult.message || `Esperava-se criar ${plannedDocs} documento(s), mas 0 foram criados.`}
                        </p>
                        {processResult.stock_issues_processed === 0 && (
                          <p className="text-xs text-slate-600 mt-3 bg-white rounded p-2">
                            Os Stock Issues podem já não estar elegíveis ou houve erro interno no processamento.
                          </p>
                        )}
                      </div>
                      
                      {/* Summary of what was expected vs actual */}
                      <div className="bg-slate-50 rounded-lg p-4 text-sm">
                        <div className="font-medium text-slate-700 mb-2">Resumo</div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs text-slate-500">Planeado</div>
                            <div className="font-medium">{plannedDocs} documento(s)</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500">Criado</div>
                            <div className="font-medium text-amber-600">{createdDocs} documento(s)</div>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                } else {
                  // Error case
                  return (
                    <>
                      {/* Error View */}
                      <div className="p-6 bg-red-50 rounded-lg text-center">
                        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                        {processResult.error === 'stale_plan' ? (
                          <>
                            <p className="font-medium text-red-800">Plano Desatualizado</p>
                            <p className="text-sm text-red-600 mt-2">{processResult.message}</p>
                            <div className="mt-3 text-xs text-slate-600 bg-white rounded p-2">
                              <p>Original: {processResult.original?.documents} documentos, {processResult.original?.issues} issues</p>
                              <p>Atual: {processResult.current?.documents} documentos, {processResult.current?.issues} issues</p>
                            </div>
                          </>
                        ) : processResult.error === 'processing_in_progress' ? (
                          <>
                            <p className="font-medium text-red-800">Processamento em Curso</p>
                            <p className="text-sm text-red-600 mt-2">{processResult.message}</p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-red-800">Erro no Processamento</p>
                            <p className="text-sm text-red-600 mt-2">{processResult.message || processResult.error}</p>
                          </>
                        )}
                      </div>
                      
                      {/* Blocked issues if any */}
                      {processResult.blocked_issues?.length > 0 && (
                        <div className="space-y-3">
                          <h3 className="font-semibold text-amber-700 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Issues Bloqueados ({processResult.blocked_issues.length})
                          </h3>
                          <div className="border border-amber-200 rounded-lg p-3 text-sm">
                            {processResult.blocked_issues.map((blocked, bIdx) => (
                              <div key={bIdx} className="flex justify-between py-1">
                                <span>{blocked.original_sku} → {blocked.effective_sku}</span>
                                <span className="text-amber-600">{blocked.reason}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                }
              })()}

              {processResult.errors?.length > 0 && (
                <div className="space-y-2" data-testid="process-errors-list">
                  <h3 className="font-semibold text-red-700 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Erros devolvidos ({processResult.errors.length})
                  </h3>
                  <div className="border border-red-200 rounded-lg divide-y divide-red-100 text-xs max-h-72 overflow-y-auto">
                    {processResult.errors.map((err, eIdx) => (
                      <div key={eIdx} className="p-2" data-testid={`process-error-${eIdx}`}>
                        <div className="font-mono text-slate-700">
                          {[err.doc, err.batch_id || err.batch || err.group || err.stage].filter(Boolean).join(' · ')}
                        </div>
                        <div className="text-red-700 mt-0.5 whitespace-pre-wrap">{err.error}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={handleClosePreviewModal} 
                  className="flex-1"
                  variant={processResult?.success ? "default" : "outline"}
                  data-testid="close-result-btn"
                >
                  Fechar
                </Button>
                {!processResult?.success && processResult?.error === 'stale_plan' && (
                  <Button 
                    onClick={handleSagePreview} 
                    className="flex-1"
                    data-testid="refresh-preview-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Atualizar Pré-visualização
                  </Button>
                )}
              </div>
            </div>
          )}
          
          {/* PREVIEW VIEW (unified - batches + stock issues) */}
          {!processResultMode && previewResult && (
            <div className="space-y-6">
              {previewResult.error ? (
                <div className="p-4 bg-red-50 rounded-lg text-red-700">
                  {previewResult.error}
                </div>
              ) : (
                <>
                  {/* WinTouch Batches Section */}
                  {(previewResult.pending_batches || 0) > 0 && (
                    <div className="border rounded-lg p-4 bg-slate-50">
                      <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Movimentos WinTouch Pendentes
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                          <div className="text-xl font-bold text-slate-800">
                            {previewResult.pending_batches || 0}
                          </div>
                          <div className="text-xs text-slate-500">Batches</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                          <div className="text-xl font-bold text-red-600">
                            {previewResult.batch_out_units || 0}
                          </div>
                          <div className="text-xs text-slate-500">Unidades OUT</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                          <div className="text-xl font-bold text-emerald-600">
                            {previewResult.batch_in_units || 0}
                          </div>
                          <div className="text-xs text-slate-500">Unidades IN</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                          <div className="text-xl font-bold text-blue-600">
                            {(previewResult.stores || []).length}
                          </div>
                          <div className="text-xs text-slate-500">Lojas</div>
                        </div>
                      </div>
                      {previewResult.date_range?.min && (
                        <div className="mt-2 text-xs text-slate-500">
                          Período: {previewResult.date_range.min} — {previewResult.date_range.max}
                        </div>
                      )}
                      {previewResult.stores?.length > 0 && (
                        <div className="mt-1 text-xs text-slate-500">
                          Lojas: {previewResult.stores.join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Stock Issues Section */}
                  {((previewResult.ready_stock_issues || 0) > 0 || (previewResult.issues_processed || 0) > 0 || (previewResult.issues_blocked || 0) > 0) && (
                    <div className="border rounded-lg p-4 bg-purple-50">
                      <h3 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Stock Issues (Complementares)
                      </h3>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                          <div className="text-xl font-bold text-purple-700">
                            {previewResult.issues_processed || previewResult.ready_stock_issues || 0}
                          </div>
                          <div className="text-xs text-slate-500">Elegíveis</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                          <div className="text-xl font-bold text-amber-600">
                            {previewResult.issues_blocked || 0}
                          </div>
                          <div className="text-xs text-slate-500">Bloqueados</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                          <div className="text-xl font-bold text-emerald-600">
                            {(previewResult.stock_issue_documents || previewResult.documents || []).length}
                          </div>
                          <div className="text-xs text-slate-500">Docs a criar</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Combined Summary */}
                  <div className="border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50">
                    <h3 className="font-semibold text-emerald-800 mb-3">Resumo Total</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                        <div className="text-2xl font-bold text-emerald-700">
                          {previewResult.total_processable || ((previewResult.pending_batches || 0) + (previewResult.ready_stock_issues || 0))}
                        </div>
                        <div className="text-xs text-emerald-600">Total a processar</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                        <div className="text-2xl font-bold text-slate-700">
                          {previewResult.isl_created || previewResult.pending_batches || 0}
                        </div>
                        <div className="text-xs text-slate-500">ISL a criar</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                        <div className="text-2xl font-bold text-blue-700">
                          {previewResult.iel_created || 0}
                        </div>
                        <div className="text-xs text-slate-500">IEL a criar</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                        <div className="text-2xl font-bold text-slate-800">
                          {previewResult.total_units_posted || previewResult.total_units || 0}
                        </div>
                        <div className="text-xs text-slate-500">Unidades totais</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* No content message */}
                  {(previewResult.total_processable || 0) === 0 && (previewResult.pending_batches || 0) === 0 && (previewResult.ready_stock_issues || 0) === 0 && (
                    <div className="p-6 bg-slate-50 rounded-lg text-center">
                      <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">Sem movimentos pendentes para processar</p>
                      <p className="text-slate-500 text-sm mt-1">Todos os batches e Stock Issues já foram processados.</p>
                    </div>
                  )}
                  
                  {/* Unknown SKUs warning */}
                  {(previewResult.unknown_skus || 0) > 0 && (
                    <div className="p-3 bg-orange-50 rounded-lg text-orange-700 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      <span>{previewResult.unknown_skus} artigos por resolver (SKU desconhecidos)</span>
                    </div>
                  )}
                  
                  {/* Simulated Documents from Stock Issues */}
                  {(previewResult.stock_issue_documents || previewResult.documents || []).length > 0 && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Documentos Complementares Simulados
                      </h3>
                      
                      {(previewResult.stock_issue_documents || previewResult.documents || []).map((doc, idx) => (
                        <div key={idx} className="border rounded-lg overflow-hidden">
                          {/* Document Header */}
                          <div className="bg-slate-50 px-4 py-3 border-b">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="font-mono bg-blue-50 text-blue-700 border-blue-200">
                                  {doc.document_type || `${doc.movement_type} Complementar`}
                                </Badge>
                                <span className="text-xs text-slate-400">— Simulação</span>
                              </div>
                            </div>
                            
                            {/* Document Details Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                              <div>
                                <span className="text-slate-500 text-xs">Loja:</span>
                                <div className="font-medium">{getStoreName(doc.store_code)}</div>
                              </div>
                              <div>
                                <span className="text-slate-500 text-xs">Período:</span>
                                <div className="font-medium">
                                  {doc.period ? (
                                    // Format YYYY-MM to "Mês YYYY"
                                    (() => {
                                      const [y, m] = doc.period.split('-');
                                      const months = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
                                      return `${months[parseInt(m)-1]} ${y}`;
                                    })()
                                  ) : doc.business_date}
                                </div>
                              </div>
                              <div>
                                <span className="text-slate-500 text-xs">Data planeada:</span>
                                <div className="font-medium">
                                  {doc.document_date ? (
                                    // Format YYYY-MM-DD to DD/MM/YYYY
                                    doc.document_date.split('-').reverse().join('/')
                                  ) : '—'}
                                </div>
                              </div>
                              <div>
                                <span className="text-slate-500 text-xs">Warehouse:</span>
                                <div className="font-medium">{doc.warehouse || '012'}</div>
                              </div>
                              <div>
                                <span className="text-slate-500 text-xs">VossoNumero planeado:</span>
                                <code className="px-2 py-0.5 bg-slate-200 rounded font-mono text-xs">
                                  {doc.vosso_numero}
                                </code>
                              </div>
                              <div>
                                <span className="text-slate-500 text-xs">Documento SAGE:</span>
                                <div className="text-amber-600 font-medium text-xs">A atribuir</div>
                              </div>
                            </div>
                            
                            {/* Business dates included */}
                            {doc.business_dates_included?.length > 0 && (
                              <div className="mt-3 pt-2 border-t border-slate-200">
                                <span className="text-xs text-slate-500">Datas originais incluídas: </span>
                                <span className="text-xs font-medium">
                                  {doc.business_dates_included.map(d => d.split('-').reverse().join('/')).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Lines */}
                          {doc.lines?.length > 0 && (
                            <div className="p-4 bg-white">
                              <div className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">
                                Linhas SAGE
                              </div>
                              <table className="w-full text-sm">
                                <thead className="text-xs text-slate-500 bg-slate-50">
                                  <tr>
                                    <th className="px-2 py-1 text-left">SKU SAGE</th>
                                    <th className="px-2 py-1 text-right">Quantidade</th>
                                    <th className="px-2 py-1 text-right">Issues</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {doc.lines.map((line, lineIdx) => (
                                    <tr key={lineIdx} className="border-t">
                                      <td className="px-2 py-2">
                                        <code className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded text-xs">
                                          {line.effective_sage_sku}
                                        </code>
                                      </td>
                                      <td className="px-2 py-2 text-right font-medium">
                                        {line.quantity}
                                      </td>
                                      <td className="px-2 py-2 text-right text-slate-500">
                                        {line.issues_count}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          
                          {/* Allocations / Origins */}
                          {doc.allocations?.length > 0 && (
                            <div className="px-4 pb-4">
                              <div className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide mt-3">
                                Origens / Allocations
                              </div>
                              <div className="space-y-2">
                                {doc.allocations.map((alloc, allocIdx) => (
                                  <div key={allocIdx} className="flex items-center justify-between text-xs bg-slate-50 rounded p-2">
                                    <div className="flex items-center gap-2">
                                      <code className="px-1.5 py-0.5 bg-slate-200 rounded">
                                        {alloc.original_sku}
                                      </code>
                                      {alloc.effective_sage_sku !== alloc.original_sku && (
                                        <>
                                          <ArrowRight className="w-3 h-3 text-slate-400" />
                                          <code className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                                            {alloc.effective_sage_sku}
                                          </code>
                                        </>
                                      )}
                                      <span className="font-medium">{alloc.quantity} un.</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      {/* Original business_date */}
                                      <span className="text-slate-500">
                                        Data original: <span className="font-medium text-slate-700">
                                          {alloc.business_date ? alloc.business_date.split('-').reverse().join('/') : '—'}
                                        </span>
                                      </span>
                                      {alloc.correction_type && (
                                        <Badge variant="outline" className="text-xs">
                                          {alloc.correction_type === 'ONE_TIME' ? 'One-time' : 'Permanente'}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Document totals */}
                          <div className="bg-slate-100 px-4 py-2 text-xs text-slate-600 flex gap-6">
                            <span>Linhas: {doc.lines?.length || 0}</span>
                            <span>Unidades: {doc.units_posted || 0}</span>
                            <span>Issues: {doc.allocations?.length || 0}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Blocked Issues */}
                  {previewResult.blocked_issues?.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="font-semibold text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Stock Issues Bloqueados ({previewResult.blocked_issues.length})
                      </h3>
                      <div className="border border-amber-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-amber-50 text-amber-800 text-xs">
                            <tr>
                              <th className="px-3 py-2 text-left">SKU Original</th>
                              <th className="px-3 py-2 text-left">SKU Efetivo</th>
                              <th className="px-3 py-2 text-right">Pendente</th>
                              <th className="px-3 py-2 text-right">Stock</th>
                              <th className="px-3 py-2 text-left">Motivo</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white">
                            {previewResult.blocked_issues.map((blocked, bIdx) => (
                              <tr key={bIdx} className="border-t border-amber-100">
                                <td className="px-3 py-2">
                                  <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">
                                    {blocked.original_sku}
                                  </code>
                                </td>
                                <td className="px-3 py-2">
                                  <code className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs">
                                    {blocked.effective_sku}
                                  </code>
                                </td>
                                <td className="px-3 py-2 text-right font-medium">
                                  {blocked.required}
                                </td>
                                <td className="px-3 py-2 text-right text-red-600">
                                  {blocked.available ?? '—'}
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600 max-w-[200px]">
                                  {blocked.reason}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Reconciliation summary */}
                  <div className="bg-slate-50 rounded-lg p-4 text-sm">
                    <div className="font-medium text-slate-700 mb-2">Reconciliação</div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-lg font-bold text-emerald-600">
                          {previewResult.total_units_posted || 0}
                        </div>
                        <div className="text-xs text-slate-500">Elegíveis</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-amber-600">
                          {previewResult.blocked_issues?.reduce((sum, b) => sum + (b.required || 0), 0) || 0}
                        </div>
                        <div className="text-xs text-slate-500">Bloqueadas</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-slate-700">
                          {(previewResult.total_units_posted || 0) + 
                           (previewResult.blocked_issues?.reduce((sum, b) => sum + (b.required || 0), 0) || 0)}
                        </div>
                        <div className="text-xs text-slate-500">Total Pendente</div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={handleClosePreviewModal} 
                  className="flex-1"
                  variant="outline"
                  data-testid="close-preview-btn"
                >
                  Fechar
                </Button>
                {canProcessFromPreview && !previewResult?.error && (
                  <Button 
                    onClick={handleShowConfirmation}
                    disabled={isProcessingFromPreview}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="process-from-preview-btn"
                  >
                    {isProcessingFromPreview ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        A processar...
                      </>
                    ) : (
                      <>
                        <Package className="w-4 h-4 mr-2" />
                        Processar movimentos SAGE
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full max-w-7xl grid-cols-7">
          <TabsTrigger value="audit-log" data-testid="tab-audit-log">
            <FileText className="w-4 h-4 mr-2" />
            Log de Auditoria
          </TabsTrigger>
          <TabsTrigger value="wintouch-batches" data-testid="tab-wintouch-batches">
            <Package className="w-4 h-4 mr-2" />
            Importações XML
          </TabsTrigger>
          <TabsTrigger value="onedrive-files" data-testid="tab-onedrive-files">
            <Cloud className="w-4 h-4 mr-2" />
            Ficheiros OneDrive
          </TabsTrigger>
          <TabsTrigger value="sku-exceptions" data-testid="tab-sku-exceptions" className="relative">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Artigos por Resolver
            <Badge 
              variant={unresolvedCount > 0 ? "destructive" : "secondary"} 
              className="ml-2 h-5 min-w-[20px] px-1.5"
              data-testid="unresolved-badge"
            >
              {unresolvedCount}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="stock-issues" data-testid="tab-stock-issues" className="relative">
            <Package className="w-4 h-4 mr-2" />
            Stocks por Resolver
            <Badge 
              variant={stockIssuesCount > 0 ? "destructive" : "secondary"} 
              className="ml-2 h-5 min-w-[20px] px-1.5"
              data-testid="stock-issues-badge"
            >
              {stockIssuesCount}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="sage-errors" data-testid="tab-sage-errors">
            <XCircle className="w-4 h-4 mr-2" />
            Erros SAGE
          </TabsTrigger>
          <TabsTrigger value="quarantine" data-testid="tab-quarantine" className="relative">
            <ShieldAlert className="w-4 h-4 mr-2" />
            Por validar
            <Badge variant={quarantineCount > 0 ? "destructive" : "secondary"} className="ml-2 h-5 min-w-[20px] px-1.5 text-xs" data-testid="quarantine-count-badge">
              {quarantineCount}
            </Badge>
          </TabsTrigger>
        </TabsList>
        
        {/* Tab 1: Log de Auditoria */}
        <TabsContent value="audit-log" className="mt-6">
          <div className="space-y-6">
            {/* Refresh Button */}
            <div className="flex justify-end">
              <Button variant="outline" onClick={loadAuditLog} data-testid="refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>
      
            {/* Filters */}
            <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <Label className="text-xs">Loja</Label>
              <Select value={selectedStore || "all"} onValueChange={(v) => setSelectedStore(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-store">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {stores.map(store => (
                    <SelectItem key={store} value={store}>{getStoreName(store)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs">Tipo de Entidade</Label>
              <Select value={entityType || "all"} onValueChange={(v) => setEntityType(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-entity-type">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="closure">Fecho</SelectItem>
                  <SelectItem value="deposit">Depósito</SelectItem>
                  <SelectItem value="config">Configuração</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs">Ação</Label>
              <Select value={action || "all"} onValueChange={(v) => setAction(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-action">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {Object.entries(ACTION_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs">ID da Entidade</Label>
              <Input
                placeholder="ID específico..."
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                className="h-10"
                data-testid="filter-entity-id"
              />
            </div>
            
            <div className="flex items-end">
              <Button variant="ghost" onClick={clearFilters} className="w-full">
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Results */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Registos ({filteredEntries.length})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-12" data-testid="audit-log-error">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
                <p className="text-red-700 font-medium">Erro ao carregar auditoria</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
                <Button variant="outline" onClick={loadAuditLog} className="mt-3">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Tentar novamente
                </Button>
              </div>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Nenhum registo encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[150px]">Data/Hora</TableHead>
                    <TableHead>Loja</TableHead>
                    <TableHead>Utilizador</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Entidade</TableHead>
                    <TableHead>Antes → Depois</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry, idx) => (
                    <TableRow key={entry.id || idx}>
                      <TableCell className="font-mono text-xs">
                        {entry.timestamp 
                          ? format(new Date(entry.timestamp), "dd/MM/yy HH:mm", { locale: pt })
                          : '—'}
                      </TableCell>
                      <TableCell className="font-medium">{getStoreName(entry.store_code) || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {entry.user_email?.split('@')[0] || entry.user_id?.slice(0, 8) || '—'}
                      </TableCell>
                      <TableCell>
                        <ActionBadge action={entry.action} />
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">
                          <span className="font-medium">{ENTITY_LABELS[entry.entity_type] || entry.entity_type}</span>
                          <span className="text-slate-400 ml-1 font-mono">{entry.entity_id?.slice(0, 8)}...</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <ValueDiff before={entry.before_value} after={entry.after_value} />
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 max-w-[150px] truncate">
                        {entry.reason || '—'}
                      </TableCell>
                      <TableCell>
                        <AuditDetailDialog entry={entry} getStoreName={getStoreName} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
          </div>
        </TabsContent>
        
        {/* Tab 2: Importações WinTouch XML - Visão Mensal */}
        <TabsContent value="wintouch-batches" className="mt-6">
          <div className="space-y-4">
            <WinTouchAutomation />
            {/* Header: Month Navigation + Filters + Refresh */}
            <div className="flex flex-col md:flex-row justify-between gap-4">
              {/* Month Navigator */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleMonthChange('prev')}
                  data-testid="month-prev-btn"
                >
                  <ChevronRight className="w-4 h-4 rotate-180" />
                </Button>
                <span className="text-sm font-medium min-w-[140px] text-center">
                  {getMonthName(batchMonth.month)} {batchMonth.year}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleMonthChange('next')}
                  data-testid="month-next-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Filters: Store + Status */}
              <div className="flex gap-4 items-center flex-wrap">
                {/* Store Filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-slate-500">Loja:</label>
                  <select
                    value={batchStoreFilter}
                    onChange={(e) => handleBatchStoreFilterChange(e.target.value)}
                    className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-slate-200"
                    data-testid="batch-store-filter"
                  >
                    <option value="">Todas as lojas</option>
                    {Array.isArray(storeMapping) && storeMapping.map((store) => (
                      <option key={store.wintouch_store_code} value={store.wintouch_store_code}>
                        {store.name || store.wintouch_store_code}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Status Filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-slate-500">Estado:</label>
                  <select
                    value={batchCategory}
                    onChange={(e) => handleBatchCategoryChange(e.target.value)}
                    className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-slate-200"
                    data-testid="batch-status-filter"
                  >
                    <option value="">Todos</option>
                    <option value="attention">Atenção Necessária</option>
                    <option value="pending">Pendentes</option>
                    <option value="integrated">Integrados</option>
                    <option value="sku_errors">Erro SKU</option>
                    <option value="stock_errors">Erro Stocks</option>
                  </select>
                </div>
                
                <Button variant="outline" onClick={() => loadWintouchBatches(batchCategory, batchStoreFilter, 0, batchMonth)} data-testid="refresh-batches-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${wintouchBatchesLoading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </div>
            
            {/* Quick Filter Badges */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleBatchCategoryChange('')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  batchCategory === ''
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid="batch-filter-all"
              >
                Todos
                <Badge className="ml-2 bg-white/20 text-inherit text-xs">
                  {batchCounts.attention + batchCounts.pending + batchCounts.integrated}
                </Badge>
              </button>
              <button
                onClick={() => handleBatchCategoryChange('attention')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  batchCategory === 'attention'
                    ? 'bg-red-600 text-white'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
                data-testid="batch-filter-attention"
              >
                <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                Atenção
                <Badge className="ml-2 bg-white/20 text-inherit text-xs">
                  {batchCounts.attention}
                </Badge>
              </button>
              <button
                onClick={() => handleBatchCategoryChange('pending')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  batchCategory === 'pending'
                    ? 'bg-amber-600 text-white'
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                }`}
                data-testid="batch-filter-pending"
              >
                <Clock className="w-3.5 h-3.5 inline mr-1" />
                Pendentes
                <Badge className="ml-2 bg-white/20 text-inherit text-xs">
                  {batchCounts.pending}
                </Badge>
              </button>
              <button
                onClick={() => handleBatchCategoryChange('integrated')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  batchCategory === 'integrated'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                }`}
                data-testid="batch-filter-integrated"
              >
                <CheckCircle className="w-3.5 h-3.5 inline mr-1" />
                Integrados
                <Badge className="ml-2 bg-white/20 text-inherit text-xs">
                  {batchCounts.integrated}
                </Badge>
              </button>
              
              {/* Separator */}
              <div className="h-6 w-px bg-slate-300 mx-1"></div>
              
              {/* SKU Errors Filter */}
              <button
                onClick={() => handleBatchCategoryChange('sku_errors')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  batchCategory === 'sku_errors'
                    ? 'bg-orange-600 text-white'
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
                data-testid="batch-filter-sku-errors"
              >
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                Erro SKU
                <Badge className="ml-2 bg-white/20 text-inherit text-xs">
                  {batchCounts.sku_errors || 0}
                </Badge>
              </button>
              
              {/* Stock Errors Filter */}
              <button
                onClick={() => handleBatchCategoryChange('stock_errors')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  batchCategory === 'stock_errors'
                    ? 'bg-amber-600 text-white'
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                }`}
                data-testid="batch-filter-stock-errors"
              >
                <Package className="w-3.5 h-3.5 inline mr-1" />
                Erro Stocks
                <Badge className="ml-2 bg-white/20 text-inherit text-xs">
                  {batchCounts.stock_errors || 0}
                </Badge>
              </button>
            </div>
            
            {/* Unified Batches Table */}
            <Card>
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {getMonthName(batchMonth.month)} {batchMonth.year}
                  </CardTitle>
                  <span className="text-sm text-slate-500">
                    {batchTotal} {batchTotal === 1 ? 'resultado' : 'resultados'}
                    {batchStoreFilter && ` em ${getStoreName(batchStoreFilter)}`}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                {wintouchBatchesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
                  </div>
                ) : wintouchBatches.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Sem importações para {getMonthName(batchMonth.month)} {batchMonth.year}</p>
                    {batchCategory && (
                      <p className="text-xs mt-1">Filtro activo: {batchCategory === 'attention' ? 'Atenção Necessária' : batchCategory === 'pending' ? 'Pendentes' : 'Integrados'}</p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[60px]">Dia</TableHead>
                            <TableHead className="w-[160px]">Loja</TableHead>
                            <TableHead className="w-[100px]">Estado</TableHead>
                            <TableHead className="min-w-[180px]">Motivo / Situação</TableHead>
                            <TableHead className="text-center w-[100px]">ISL</TableHead>
                            <TableHead className="text-center w-[100px]">IEL</TableHead>
                            <TableHead className="w-[50px]">Doc</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {wintouchBatches.map((batch, idx) => (
                            <TableRow 
                              key={batch.batch_id || idx}
                              className={
                                batch._category === 'attention' ? 'bg-red-50/30' : 
                                batch._category === 'pending' ? 'bg-amber-50/20' : ''
                              }
                            >
                              {/* Day column */}
                              <TableCell className="font-mono text-sm font-medium">
                                {batch.business_date 
                                  ? batch.business_date.split('-')[2]
                                  : '—'}
                              </TableCell>
                              
                              {/* Store column */}
                              <TableCell className="text-sm">
                                {getStoreName(batch.store_code) || batch.store_code || '—'}
                              </TableCell>
                              
                              {/* Status badge column */}
                              <TableCell>
                                <Badge className={
                                  batch._category === 'attention' ? 'bg-red-100 text-red-700' :
                                  batch._category === 'pending' ? 'bg-amber-100 text-amber-700' :
                                  'bg-emerald-100 text-emerald-700'
                                }>
                                  {batch._category === 'attention' ? 'Atenção' :
                                   batch._category === 'pending' ? 'Pendente' : 'Integrado'}
                                </Badge>
                              </TableCell>
                              
                              {/* Reason/Situation column */}
                              <TableCell>
                                <span className={`text-xs ${
                                  batch._category === 'attention' ? 'text-red-600' :
                                  batch._category === 'pending' ? 'text-amber-600' :
                                  'text-slate-500'
                                }`}>
                                  {getBatchReason(batch)}
                                </span>
                              </TableCell>
                              
                              {/* ISL reference column */}
                              <TableCell className="text-center">
                                {batch.sage_isl_reference ? (
                                  <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                                    {batch.sage_isl_reference}
                                  </code>
                                ) : batch.isl_required ? (
                                  <span className="text-slate-400">—</span>
                                ) : (
                                  <span className="text-slate-300 text-xs">N/A</span>
                                )}
                              </TableCell>
                              
                              {/* IEL reference column */}
                              <TableCell className="text-center">
                                {batch.sage_iel_reference ? (
                                  <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                                    {batch.sage_iel_reference}
                                  </code>
                                ) : batch.iel_required ? (
                                  <span className="text-slate-400">—</span>
                                ) : (
                                  <span className="text-slate-300 text-xs">N/A</span>
                                )}
                              </TableCell>
                              
                              {/* Action column */}
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenBatchDetail(batch)}
                                  data-testid={`batch-detail-btn-${batch.batch_id}`}
                                  title="Ver documentos"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Pagination */}
                    {batchTotal > batchLimit && (
                      <div className="flex items-center justify-between pt-4 border-t mt-4">
                        <span className="text-sm text-slate-500">
                          A mostrar {batchSkip + 1}–{Math.min(batchSkip + batchLimit, batchTotal)} de {batchTotal}
                        </span>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBatchPageChange('prev')}
                            disabled={batchSkip === 0}
                          >
                            Anterior
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBatchPageChange('next')}
                            disabled={batchSkip + batchLimit >= batchTotal}
                          >
                            Seguinte
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Batch Detail Modal */}
        <Dialog open={showBatchDetail} onOpenChange={(open) => { setShowBatchDetail(open); if (!open) setBatchIssues(null); }}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Detalhe da Importação XML
              </DialogTitle>
            </DialogHeader>
            
            {selectedBatch && (
              <div className="space-y-6">
                {/* RESUMO Section */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Resumo
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500 text-xs">Loja:</span>
                      <div className="font-medium">{getStoreName(selectedBatch.store_code) || selectedBatch.store_code}</div>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs">Data:</span>
                      <div className="font-medium">
                        {selectedBatch.business_date?.split('-').reverse().join('/') || '—'}
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs">Estado:</span>
                      <div>
                        <Badge className={getBatchStatusColor(selectedBatch.status, selectedBatch._category)}>
                          {getBatchStatusLabel(selectedBatch.status)}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs">Categoria:</span>
                      <div>
                        <Badge 
                          className={
                            selectedBatch._category === 'attention' ? 'bg-red-100 text-red-700' :
                            selectedBatch._category === 'integrated' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-amber-100 text-amber-700'
                          }
                        >
                          {getCategoryLabel(selectedBatch._category)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* DOCUMENTOS SAGE Section */}
                <div className="bg-emerald-50 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Documentos SAGE
                  </h3>
                  <div className="space-y-3">
                    {/* ISL */}
                    {selectedBatch.isl_required && (
                      <div className="flex items-center justify-between bg-white p-3 rounded border border-emerald-200">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-emerald-600 text-white">ISL</Badge>
                          {selectedBatch.sage_isl_reference ? (
                            <code className="font-mono text-emerald-800 font-medium">
                              {selectedBatch.sage_isl_reference}
                            </code>
                          ) : (
                            <span className="text-slate-500 text-sm">Não criado</span>
                          )}
                        </div>
                        {selectedBatch.isl_posted || selectedBatch.sage_isl_reference ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-amber-500" />
                        )}
                      </div>
                    )}
                    
                    {/* Complementary ISL created from resolved Stock Issues */}
                    {(batchIssues?.stock_issues || []).filter(si => si.sage_document_reference).map(si => (
                      <div key={si.issue_id} className="flex items-center justify-between bg-white p-3 rounded border border-emerald-200" data-testid={`complementary-doc-${si.issue_id}`}>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">ISL compl.</Badge>
                          <code className="font-mono text-emerald-800 font-medium">{si.sage_document_reference}</code>
                          <span className="text-xs text-slate-500">
                            {si.original_sku || si.sku}{si.corrected_sage_sku ? ` → ${si.corrected_sage_sku}` : ''}
                          </span>
                        </div>
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                    ))}
                    
                    {/* IEL */}
                    {selectedBatch.iel_required && (
                      <div className="flex items-center justify-between bg-white p-3 rounded border border-blue-200">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-blue-600 text-white">IEL</Badge>
                          {selectedBatch.sage_iel_reference ? (
                            <code className="font-mono text-blue-800 font-medium">
                              {selectedBatch.sage_iel_reference}
                            </code>
                          ) : (
                            <span className="text-slate-500 text-sm">Não criado</span>
                          )}
                        </div>
                        {selectedBatch.iel_posted || selectedBatch.sage_iel_reference ? (
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-amber-500" />
                        )}
                      </div>
                    )}
                    
                    {/* No SAGE docs required */}
                    {!selectedBatch.isl_required && !selectedBatch.iel_required && (
                      <div className="text-center py-4 text-slate-500 bg-white rounded border">
                        <p className="text-sm">Nenhum documento SAGE requerido</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* PROBLEMAS ENCONTRADOS Section */}
                {(selectedBatch._category === 'attention' || (selectedBatch._attention_reasons || []).length > 0) && (
                  <div className="bg-red-50 rounded-lg p-4">
                    <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Problemas Encontrados
                    </h3>
                    
                    {batchIssuesLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-5 h-5 text-red-400 animate-spin" />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Tabs for different error types */}
                        <Tabs defaultValue={
                          (selectedBatch.unknown_skus || []).length > 0 ? "sku" :
                          batchIssues?.stock_issues?.length > 0 ? "stock" :
                          batchIssues?.sku_exceptions?.length > 0 ? "resolver" : "sku"
                        } className="w-full">
                          <TabsList className="grid w-full grid-cols-3 bg-red-100/50">
                            <TabsTrigger 
                              value="sku" 
                              className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs"
                            >
                              Erro SKU {(selectedBatch.unknown_skus || []).length > 0 && (
                                <span className="ml-1 bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded-full text-xs">
                                  {selectedBatch.unknown_skus.length}
                                </span>
                              )}
                            </TabsTrigger>
                            <TabsTrigger 
                              value="stock" 
                              className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-xs"
                            >
                              Erro Stocks {batchIssues?.stock_issues?.length > 0 && (
                                <span className="ml-1 bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full text-xs">
                                  {batchIssues.stock_issues.length}
                                </span>
                              )}
                            </TabsTrigger>
                            <TabsTrigger 
                              value="resolver" 
                              className="data-[state=active]:bg-red-500 data-[state=active]:text-white text-xs"
                            >
                              Por Resolver {batchIssues?.sku_exceptions?.length > 0 && (
                                <span className="ml-1 bg-red-200 text-red-800 px-1.5 py-0.5 rounded-full text-xs">
                                  {batchIssues.sku_exceptions.length}
                                </span>
                              )}
                            </TabsTrigger>
                          </TabsList>
                          
                          {/* Tab: Erro SKU - SKUs desconhecidos */}
                          <TabsContent value="sku" className="mt-3">
                            {(selectedBatch.unknown_skus || []).length > 0 ? (
                              <div className="bg-white rounded p-3 border border-orange-200">
                                <div className="flex justify-between items-center mb-2">
                                  <div className="font-medium text-orange-800 text-sm">
                                    SKUs Desconhecidos ({selectedBatch.unknown_skus.length})
                                  </div>
                                  <a 
                                    href="/lojas/auditoria?tab=sku-exceptions"
                                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                  >
                                    Ver todos em "Artigos por Resolver" →
                                  </a>
                                </div>
                                <div className="space-y-2">
                                  {selectedBatch.unknown_skus.slice(0, 10).map((sku, i) => (
                                    <div key={i} className="text-xs bg-orange-50 p-2 rounded border border-orange-100">
                                      <div className="flex justify-between items-center">
                                        <div>
                                          <span className="text-slate-500">SKU:</span>
                                          <code className="ml-1 font-mono text-orange-700 font-bold">{sku}</code>
                                        </div>
                                        <span className="text-orange-600 text-xs">
                                          Não existe no catálogo SAGE
                                        </span>
                                      </div>
                                      <div className="mt-2 flex gap-2">
                                        <a 
                                          href={`/lojas/auditoria?tab=sku-exceptions&search=${encodeURIComponent(sku)}`}
                                          className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                                        >
                                          Criar mapeamento
                                        </a>
                                        <a 
                                          href={`/inventory?search=${encodeURIComponent(sku)}`}
                                          className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded hover:bg-slate-200"
                                        >
                                          Pesquisar no catálogo
                                        </a>
                                      </div>
                                    </div>
                                  ))}
                                  {selectedBatch.unknown_skus.length > 10 && (
                                    <div className="text-xs text-orange-500 text-center">
                                      +{selectedBatch.unknown_skus.length - 10} mais SKUs
                                    </div>
                                  )}
                                </div>
                                <div className="mt-3 p-2 bg-orange-100 rounded text-xs text-orange-800">
                                  <strong>Como resolver:</strong>
                                  <ol className="list-decimal ml-4 mt-1 space-y-1">
                                    <li>Clique em "Criar mapeamento" para associar este SKU a um artigo SAGE existente</li>
                                    <li>Ou verifique se o artigo é um <strong>Descritor/Serviço</strong> no SAGE (não gera stock)</li>
                                    <li>Depois de resolver, clique em <strong>"Reclassificar"</strong> abaixo para actualizar o batch</li>
                                  </ol>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center text-slate-500 py-4 text-sm">
                                Sem erros de SKU desconhecido
                              </div>
                            )}
                          </TabsContent>
                          
                          {/* Tab: Erro Stocks - Problemas de stock insuficiente */}
                          <TabsContent value="stock" className="mt-3">
                            {batchIssues?.stock_issues?.length > 0 ? (
                              <div className="bg-white rounded p-3 border border-amber-200">
                                <div className="font-medium text-amber-800 mb-2 text-sm">
                                  Stocks Insuficientes ({batchIssues.stock_issues.length})
                                </div>
                                <div className="space-y-2">
                                  {batchIssues.stock_issues.slice(0, 5).map((issue, i) => (
                                    <div key={i} className="text-xs bg-amber-50 p-2 rounded border border-amber-100">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <span className="text-slate-500">SKU:</span>
                                          <code className="ml-1 font-mono text-amber-700">{issue.sku}</code>
                                        </div>
                                        <Badge variant="outline" className="text-xs">
                                          {issue.status}
                                        </Badge>
                                      </div>
                                      {issue.description && (
                                        <div className="text-slate-600 mt-1 truncate">
                                          {issue.description}
                                        </div>
                                      )}
                                      <div className="grid grid-cols-2 gap-2 mt-2 text-slate-600">
                                        <div>
                                          <span className="text-slate-500">Qtd. necessária:</span>
                                          <span className="ml-1 font-medium">{issue.required_quantity}</span>
                                        </div>
                                        <div>
                                          <span className="text-slate-500">Stock disponível:</span>
                                          <span className="ml-1 font-medium">{issue.available_quantity_at_attempt ?? '—'}</span>
                                        </div>
                                        <div>
                                          <span className="text-slate-500">Em falta:</span>
                                          <span className="ml-1 font-medium text-amber-700">
                                            {issue.shortage_quantity ?? issue.pending_quantity ?? '—'}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-slate-500">Armazém:</span>
                                          <span className="ml-1 font-medium">{issue.warehouse}</span>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  {batchIssues.stock_issues.length > 5 && (
                                    <div className="text-xs text-amber-500 text-center">
                                      +{batchIssues.stock_issues.length - 5} mais stocks por resolver
                                    </div>
                                  )}
                                </div>
                                <div className="mt-3 p-2 bg-amber-100 rounded text-xs text-amber-800">
                                  <strong>Como resolver:</strong> Aguarde entrada de stock no armazém SAGE, faça uma transferência interna ou corrija o artigo.
                                </div>
                                <Button
                                  size="sm"
                                  className="mt-3 w-full bg-amber-600 hover:bg-amber-700"
                                  data-testid="goto-stock-issues-btn"
                                  onClick={() => {
                                    setStockIssuesSearch(batchIssues.stock_issues[0]?.batch_id || '');
                                    setShowBatchDetail(false);
                                    setBatchIssues(null);
                                    handleTabChange('stock-issues');
                                  }}
                                >
                                  Corrigir em "Stocks por Resolver"
                                </Button>
                              </div>
                            ) : (
                              <div className="text-center text-slate-500 py-4 text-sm">
                                Sem erros de stock
                              </div>
                            )}
                          </TabsContent>
                          
                          {/* Tab: Por Resolver - SKU Exceptions que precisam de mapeamento */}
                          <TabsContent value="resolver" className="mt-3">
                            {batchIssues?.sku_exceptions?.length > 0 ? (
                              <div className="bg-white rounded p-3 border border-red-200">
                                <div className="font-medium text-red-800 mb-2 text-sm">
                                  Artigos por Resolver ({batchIssues.sku_exceptions.length})
                                </div>
                                <div className="space-y-2">
                                  {batchIssues.sku_exceptions.slice(0, 5).map((exc, i) => (
                                    <div key={i} className="text-xs bg-red-50 p-2 rounded border border-red-100">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <span className="text-slate-500">SKU WinTouch:</span>
                                          <code className="ml-1 font-mono text-red-700">{exc.source_sku}</code>
                                        </div>
                                        <span className="text-red-600 font-medium">
                                          {exc.total_quantity || exc.occurrence_count || 1} un.
                                        </span>
                                      </div>
                                      {exc.source_description && (
                                        <div className="text-slate-600 mt-1 truncate">
                                          {exc.source_description}
                                        </div>
                                      )}
                                      <div className="text-red-500 mt-1 text-xs">
                                        Motivo: SKU não encontrado no catálogo SAGE
                                      </div>
                                      {/* Link to resolve */}
                                      <div className="mt-2 flex gap-2">
                                        <a 
                                          href={`/admin/sku-management?search=${encodeURIComponent(exc.source_sku)}`}
                                          className="text-xs text-blue-600 hover:underline"
                                        >
                                          Criar mapeamento →
                                        </a>
                                      </div>
                                    </div>
                                  ))}
                                  {batchIssues.sku_exceptions.length > 5 && (
                                    <div className="text-xs text-red-500 text-center">
                                      +{batchIssues.sku_exceptions.length - 5} mais artigos por resolver
                                    </div>
                                  )}
                                </div>
                                <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-800">
                                  <strong>Como resolver:</strong> Crie um mapeamento de SKU ou verifique se o artigo existe no SAGE.
                                </div>
                              </div>
                            ) : (
                              <div className="text-center text-slate-500 py-4 text-sm">
                                Sem artigos pendentes de resolução
                              </div>
                            )}
                          </TabsContent>
                        </Tabs>
                        
                        {/* Reclassify Button - Always visible when batch not posted */}
                        {!selectedBatch.sage_isl_reference && !selectedBatch.sage_iel_reference && (
                          <div className="bg-blue-50 rounded p-3 border border-blue-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-blue-800 text-sm">Reclassificar Batch</div>
                                <p className="text-xs text-blue-600 mt-1">
                                  Aplica as regras actuais de classificação (SKUs, mapeamentos, descritores)
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReclassifyBatch(selectedBatch.batch_id)}
                                disabled={reclassifyLoading}
                                className="border-blue-300 text-blue-700 hover:bg-blue-100"
                                data-testid="reclassify-batch-btn"
                              >
                                {reclassifyLoading ? (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    A reclassificar...
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Reclassificar
                                  </>
                                )}
                              </Button>
                            </div>
                            
                            {/* Reclassify result */}
                            {reclassifyResult && (
                              <div className={`mt-3 p-2 rounded text-xs ${reclassifyResult.success ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                                {reclassifyResult.success ? (
                                  <>
                                    <strong>Reclassificado com sucesso!</strong>
                                    <div className="mt-1">
                                      {reclassifyResult.status_changed ? (
                                        <>Estado alterado: {reclassifyResult.previous_status} → <strong>{reclassifyResult.new_status}</strong></>
                                      ) : (
                                        'Estado mantido (sem alterações)'
                                      )}
                                    </div>
                                    {reclassifyResult.previous_unknown_skus?.length > 0 && reclassifyResult.new_unknown_skus?.length === 0 && (
                                      <div className="mt-1 text-emerald-700">
                                        SKUs resolvidos: {reclassifyResult.previous_unknown_skus.join(', ')}
                                      </div>
                                    )}
                                    {reclassifyResult.new_unknown_skus?.length > 0 && (
                                      <div className="mt-1 text-amber-700">
                                        Ainda pendentes: {reclassifyResult.new_unknown_skus.join(', ')}
                                      </div>
                                    )}
                                    <div className="mt-1">
                                      Físicos: {reclassifyResult.physical_lines} | Serviços: {reclassifyResult.service_lines} | Financeiros: {reclassifyResult.financial_lines} | Desconhecidos: {reclassifyResult.unknown_lines}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <strong>Erro:</strong> {reclassifyResult.error}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Invalid SKUs from batch itself */}
                        {(selectedBatch.invalid_skus || []).length > 0 && !batchIssues?.sku_exceptions?.length && (
                          <div className="bg-white rounded p-3 border border-red-200">
                            <div className="font-medium text-red-800 mb-2 text-sm">
                              SKUs Inválidos no Batch ({selectedBatch.invalid_skus.length})
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {selectedBatch.invalid_skus.slice(0, 10).map((item, i) => (
                                <code key={i} className="text-xs bg-red-100 px-2 py-0.5 rounded text-red-700">
                                  {item.sku || item}
                                </code>
                              ))}
                              {selectedBatch.invalid_skus.length > 10 && (
                                <span className="text-xs text-red-500">
                                  +{selectedBatch.invalid_skus.length - 10} mais
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Error message - parse and display appropriately */}
                        {selectedBatch.error_message && (
                          <div className="bg-white rounded p-3 border border-red-200">
                            {/* Check if it's a SAGE stock error */}
                            {selectedBatch.error_message.toLowerCase().includes('quantidade suficiente') ||
                             selectedBatch.error_message.toLowerCase().includes('stock') ||
                             selectedBatch.error_message.toLowerCase().includes('armazém') ? (
                              <>
                                <div className="font-medium text-amber-800 mb-2 text-sm flex items-center gap-2">
                                  <Package className="w-4 h-4" />
                                  Erro de Stock no SAGE
                                </div>
                                <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded mb-2">
                                  {selectedBatch.error_message}
                                </p>
                                {/* Extract SKU from error message if possible */}
                                {(() => {
                                  const match = selectedBatch.error_message.match(/artigo\s+([A-Z0-9]+)/i);
                                  const warehouseMatch = selectedBatch.error_message.match(/armazém\s+(\d+)/i);
                                  return match ? (
                                    <div className="text-xs bg-amber-100 p-2 rounded">
                                      <strong>Artigo afectado:</strong> <code className="font-mono text-amber-800">{match[1]}</code>
                                      {warehouseMatch && <span className="ml-2">| Armazém: <code className="font-mono">{warehouseMatch[1]}</code></span>}
                                    </div>
                                  ) : null;
                                })()}
                                <div className="mt-3 p-2 bg-amber-100 rounded text-xs text-amber-800">
                                  <strong>Como resolver:</strong> Verifique o stock do artigo no SAGE. 
                                  Pode ser necessário fazer uma transferência de stock ou ajuste de inventário.
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="font-medium text-red-800 mb-1 text-sm">Erro de Processamento</div>
                                <p className="text-xs text-red-600">{selectedBatch.error_message}</p>
                                <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-800">
                                  <strong>Como resolver:</strong> Contacte o suporte ou tente executar novamente a ingestão.
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        
                        {/* No issues found in detail */}
                        {!batchIssuesLoading && !batchIssues?.sku_exceptions?.length && !batchIssues?.stock_issues?.length && 
                         !(selectedBatch.invalid_skus || []).length && !selectedBatch.error_message && (
                          <div className="text-sm text-slate-500 text-center py-2">
                            Detalhes de problemas não disponíveis
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex gap-3 pt-2">
                  <Button 
                    onClick={() => setShowBatchDetail(false)} 
                    className="flex-1"
                    variant="outline"
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Tab 3: Artigos por Resolver */}
        <TabsContent value="sku-exceptions" className="mt-6">
          <SkuExceptions />
        </TabsContent>

        <TabsContent value="quarantine" className="mt-6">
          <QuarantineDocuments onCountChange={setQuarantineCount} />
        </TabsContent>
        
        {/* Tab 4: Stocks por Resolver */}
        <TabsContent value="stock-issues" className="mt-6">
          <StockIssues initialSearch={stockIssuesSearch} />
        </TabsContent>
        
        <TabsContent value="sage-errors" className="mt-6">
          <SageErrors />
        </TabsContent>
        
        {/* Tab 5: Ficheiros OneDrive */}
        <TabsContent value="onedrive-files" className="mt-6">
          <OneDriveFiles token={localStorage.getItem('token')} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
